import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import html2pdf from 'html2pdf.js';

const PsicologoDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [evaluaciones, setEvaluaciones] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [competencias, setCompetencias] = useState([]);
  const [evaluacionesAsignadas, setEvaluacionesAsignadas] = useState([]);
  const [selectedEvaluacion, setSelectedEvaluacion] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [estadisticas, setEstadisticas] = useState({
    totalEvaluaciones: 0,
    evaluacionesCompletadas: 0,
    evaluacionesPendientes: 0,
    promedioGeneral: 0
  });

  const [competenciasModal, setCompetenciasModal] = useState([]);
  const [cargandoCompetencias, setCargandoCompetencias] = useState(false);
  const [generandoPdf, setGenerandoPdf] = useState(null); // Para manejar estado de generación de PDF
  const [datosUsuario, setDatosUsuario] = useState(null); // Para datos del psicólogo
  
  // Estados para edición del psicólogo
  const [interpretacionesEditadas, setInterpretacionesEditadas] = useState({});
  const [resumenEjecutivoEditado, setResumenEjecutivoEditado] = useState('');
  const [modoEdicion, setModoEdicion] = useState({ interpretacion: {}, resumenEjecutivo: false });
  const [guardandoCambios, setGuardandoCambios] = useState(false);
  
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await cargarDatos();
        await cargarDatosUsuario(currentUser); // Cargar datos del psicólogo
      } else {
        navigate('/login');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  const cargarDatos = async () => {
    try {
      console.log('🔄 Cargando datos reales para Dashboard Psicólogo...');
      
      // 1. Cargar evaluaciones 180° terminadas (datos principales)
      const evaluacionesSnapshot = await getDocs(collection(db, 'eval180_terminada'));
      const evaluacionesData = evaluacionesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log(`📊 Evaluaciones 180° completadas encontradas: ${evaluacionesData.length}`);
      
      // Debug: Mostrar estructura de una evaluación para entender el formato de fecha
      if (evaluacionesData.length > 0) {
        console.log('🔍 Estructura de evaluación de ejemplo:', evaluacionesData[0]);
        console.log('📅 Campo fecha_procesamiento:', evaluacionesData[0].fecha_procesamiento);
        console.log('📅 Tipo de fecha_procesamiento:', typeof evaluacionesData[0].fecha_procesamiento);
      }

      // 2. Cargar empleados para estadísticas organizacionales
      const empleadosSnapshot = await getDocs(collection(db, 'empleados'));
      const empleadosData = empleadosSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log(`👥 Total empleados en la empresa: ${empleadosData.length}`);
      setEmpleados(empleadosData);

      // 3. Cargar evaluaciones asignadas para obtener evaluadorId
      const evaluacionesAsignadasSnapshot = await getDocs(collection(db, 'evaluaciones_asignadas'));
      const evaluacionesAsignadasData = evaluacionesAsignadasSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log(`� Total evaluaciones asignadas: ${evaluacionesAsignadasData.length}`);

      // 4. Enriquecer evaluaciones con información del supervisor y empleado evaluado
      const evaluacionesEnriquecidas = evaluacionesData.map(evaluacion => {
        // Buscar la evaluación asignada correspondiente para obtener el evaluadorId y empleadoId
        const evaluacionAsignada = evaluacionesAsignadasData.find(asignada => 
          (asignada.empleadoCedula || asignada.empleado_cedula) === evaluacion.empleado_cedula
        );

        let evaluacionEnriquecida = { ...evaluacion };

        if (evaluacionAsignada) {
          // Enriquecer con datos del supervisor usando evaluadorId
          if (evaluacionAsignada.evaluadorId) {
            const supervisor = empleadosData.find(emp => emp.id === evaluacionAsignada.evaluadorId);
            
            if (supervisor) {
              console.log(`👨‍💼 Supervisor encontrado para ${evaluacion.empleado_nombre}: ${supervisor.nombre} ${supervisor.apellido} (${supervisor.cargo})`);
              evaluacionEnriquecida = {
                ...evaluacionEnriquecida,
                supervisor_nombre: supervisor.nombre,
                supervisor_apellido: supervisor.apellido,
                supervisor_cargo: supervisor.cargo,
                supervisor_cedula: supervisor.cedula,
                evaluadorId: evaluacionAsignada.evaluadorId
              };
            } else {
              console.log(`⚠️ No se encontró supervisor con ID: ${evaluacionAsignada.evaluadorId} para ${evaluacion.empleado_nombre}`);
            }
          }

          // Enriquecer con apellidos del empleado evaluado usando empleadoId
          if (evaluacionAsignada.empleadoId) {
            const empleadoCompleto = empleadosData.find(emp => emp.id === evaluacionAsignada.empleadoId);
            
            if (empleadoCompleto) {
              console.log(`👤 Empleado completo encontrado para ${evaluacion.empleado_nombre}: ${empleadoCompleto.nombre} ${empleadoCompleto.apellido} (${empleadoCompleto.cargo})`);
              evaluacionEnriquecida = {
                ...evaluacionEnriquecida,
                empleado_apellido: empleadoCompleto.apellido,
                empleado_cargo: empleadoCompleto.cargo || evaluacion.empleado_cargo,
                empleadoId: evaluacionAsignada.empleadoId
              };
            } else {
              console.log(`⚠️ No se encontró empleado con ID: ${evaluacionAsignada.empleadoId} para ${evaluacion.empleado_nombre}`);
            }
          } else {
            console.log(`⚠️ No se encontró empleadoId en evaluaciones_asignadas para empleado: ${evaluacion.empleado_nombre} (${evaluacion.empleado_cedula})`);
          }
        } else {
          console.log(`⚠️ No se encontró evaluación asignada para empleado: ${evaluacion.empleado_nombre} (${evaluacion.empleado_cedula})`);
        }

        return evaluacionEnriquecida;
      });
      
      setEvaluaciones(evaluacionesEnriquecidas);
      
      // Resumen del enriquecimiento de datos
      const empleadosConApellido = evaluacionesEnriquecidas.filter(e => e.empleado_apellido).length;
      const empleadosConSupervisor = evaluacionesEnriquecidas.filter(e => e.supervisor_nombre).length;
      console.log(`📊 Resumen de enriquecimiento:`);
      console.log(`   - Empleados con apellido enriquecido: ${empleadosConApellido}/${evaluacionesEnriquecidas.length}`);
      console.log(`   - Empleados con supervisor enriquecido: ${empleadosConSupervisor}/${evaluacionesEnriquecidas.length}`);
      
      // Debug: mostrar estructura de una evaluación asignada
      if (evaluacionesAsignadasData.length > 0) {
        console.log('🔍 Ejemplo de evaluación asignada:', evaluacionesAsignadasData[0]);
        console.log('🔍 Campos disponibles:', Object.keys(evaluacionesAsignadasData[0]));
        console.log('📋 Empleados en evaluaciones asignadas:', evaluacionesAsignadasData.map(e => ({
          id: e.id,
          empleadoCedula: e.empleadoCedula || e.empleado_cedula || e.cedula,
          empleadoNombre: e.empleadoNombre || e.empleado_nombre || 'Sin nombre',
          tieneCompetencias: !!e.competenciasDetalle,
          numCompetencias: e.competenciasDetalle ? Object.keys(e.competenciasDetalle).length : 0,
          evaluadorId: e.evaluadorId || 'Sin evaluadorId',
          empleadoId: e.empleadoId || 'Sin empleadoId'
        })));
        
        if (evaluacionesAsignadasData[0].competenciasDetalle) {
          console.log('🔍 CompetenciasDetalle keys del primer ejemplo:', Object.keys(evaluacionesAsignadasData[0].competenciasDetalle));
        }
      }
      
      setEvaluacionesAsignadas(evaluacionesAsignadasData);

      // 5. Cargar competencias (catálogo de competencias y preguntas)
      const competenciasSnapshot = await getDocs(collection(db, 'competencias'));
      const competenciasData = competenciasSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log(`🎯 Total competencias disponibles: ${competenciasData.length}`);
      setCompetencias(competenciasData);

      // 6. Cargar autoevaluaciones realizadas
      const autoevaluacionesRealizadasSnapshot = await getDocs(collection(db, 'autoevaluaciones_realizadas'));
      const autoevaluacionesRealizadas = autoevaluacionesRealizadasSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log(`✅ Autoevaluaciones realizadas: ${autoevaluacionesRealizadas.length}`);

      // 7. Cargar evaluaciones de supervisor realizadas
      const evaluacionesRealizadasSnapshot = await getDocs(collection(db, 'evaluaciones_realizadas'));
      const evaluacionesRealizadas = evaluacionesRealizadasSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log(`✅ Evaluaciones supervisor realizadas: ${evaluacionesRealizadas.length}`);

      // Calcular estadísticas consolidadas
      calcularEstadisticasCompletas(
        evaluacionesData, 
        evaluacionesAsignadasData, 
        autoevaluacionesRealizadas, 
        evaluacionesRealizadas, 
        empleadosData
      );
    } catch (error) {
      console.error('❌ Error cargando datos:', error);
    }
  };

  const cargarDatosUsuario = async (currentUser) => {
    try {
      console.log('🔄 Cargando datos del usuario psicólogo...');
      
      // Buscar en la colección usuarios por el email del usuario autenticado
      const usuariosSnapshot = await getDocs(query(
        collection(db, 'usuarios'),
        where('email', '==', currentUser.email)
      ));
      
      if (!usuariosSnapshot.empty) {
        const userData = usuariosSnapshot.docs[0].data();
        setDatosUsuario({
          nombreCompleto: `${userData.nombre || ''} ${userData.apellido || ''}`.trim(),
          cedula: userData.cedula || 'Sin cédula',
          email: userData.email || currentUser.email,
          tp: 'TP:' // Se organizará más adelante
        });
        console.log('✅ Datos del psicólogo cargados:', userData.nombre, userData.apellido);
      } else {
        // Fallback con datos básicos del auth
        setDatosUsuario({
          nombreCompleto: currentUser.displayName || 'Psicólogo',
          cedula: 'Sin cédula',
          email: currentUser.email,
          tp: 'TP:'
        });
        console.log('⚠️ No se encontraron datos adicionales del usuario en la colección usuarios');
      }
    } catch (error) {
      console.error('❌ Error cargando datos del usuario:', error);
      // Fallback básico
      setDatosUsuario({
        nombreCompleto: currentUser?.displayName || 'Psicólogo',
        cedula: 'Sin cédula',
        email: currentUser?.email || '',
        tp: 'TP:'
      });
    }
  };

  const calcularEstadisticasCompletas = (
    evaluacionesData, 
    evaluacionesAsignadas, 
    autoevaluacionesRealizadas, 
    evaluacionesRealizadas, 
    empleadosData
  ) => {
    // Todas las evaluaciones en eval180_terminada están completadas por definición
    const completadas = evaluacionesData;
    
    // LÓGICA CORRECTA: Cada evaluación asignada requiere AMBOS componentes
    const totalAsignadas = evaluacionesAsignadas.length;
    
    // Autoevaluaciones pendientes = evaluaciones_asignadas - autoevaluaciones_realizadas
    const autoevaluacionesPendientes = Math.max(0, totalAsignadas - autoevaluacionesRealizadas.length);
    
    // Evaluaciones supervisor pendientes = evaluaciones_asignadas - evaluaciones_realizadas
    const evaluacionesSupervisorPendientes = Math.max(0, totalAsignadas - evaluacionesRealizadas.length);
    
    // Total pendientes = evaluaciones que aún NO están en eval180_terminada
    // Es decir, las que no han completado AMBOS componentes (auto + supervisor)
    const totalPendientes = Math.max(0, totalAsignadas - completadas.length);
    
    // Calcular promedio ponderado general
    let promedioTotal = 0;
    if (completadas.length > 0) {
      const sumaPromedios = completadas.reduce((sum, evaluacion) => {
        return sum + (evaluacion.ponderado_final || 0);
      }, 0);
      promedioTotal = sumaPromedios / completadas.length;
    }

    // Calcular estadísticas adicionales
    const totalEmpleados = empleadosData.length;
    const empleadosEvaluados = new Set(completadas.map(e => e.empleado_cedula)).size;
    const cobertura = totalEmpleados > 0 ? (empleadosEvaluados / totalEmpleados) * 100 : 0;

    // Análisis de distribución de puntajes
    const distribucionPuntajes = {
      excelente: completadas.filter(e => (e.ponderado_final || 0) >= 4.5).length,
      bueno: completadas.filter(e => (e.ponderado_final || 0) >= 3.5 && (e.ponderado_final || 0) < 4.5).length,
      regular: completadas.filter(e => (e.ponderado_final || 0) >= 2.5 && (e.ponderado_final || 0) < 3.5).length,
      deficiente: completadas.filter(e => (e.ponderado_final || 0) < 2.5).length
    };

    console.log(`📈 Estadísticas reales calculadas con lógica corregida:`);
    console.log(`   - Total evaluaciones completadas (eval180_terminada): ${completadas.length}`);
    console.log(`   - Total evaluaciones asignadas: ${totalAsignadas}`);
    console.log(`   - Autoevaluaciones realizadas: ${autoevaluacionesRealizadas.length}`);
    console.log(`   - Evaluaciones supervisor realizadas: ${evaluacionesRealizadas.length}`);
    console.log(`   📊 CÁLCULOS PENDIENTES (CORREGIDOS):`);
    console.log(`     • Autoevaluaciones pendientes: ${totalAsignadas} - ${autoevaluacionesRealizadas.length} = ${autoevaluacionesPendientes}`);
    console.log(`     • Eval. supervisor pendientes: ${totalAsignadas} - ${evaluacionesRealizadas.length} = ${evaluacionesSupervisorPendientes}`);
    console.log(`     • Total pendientes (eval. incompletas): ${totalAsignadas} - ${completadas.length} = ${totalPendientes}`);
    console.log(`   ⚠️  NOTA: Una evaluación está pendiente hasta que tenga AMBOS componentes completados`);
    console.log(`   - Promedio ponderado general: ${promedioTotal.toFixed(2)}`);
    console.log(`   - Cobertura organizacional: ${cobertura.toFixed(1)}%`);
    console.log(`   - Empleados evaluados: ${empleadosEvaluados} de ${totalEmpleados}`);

    setEstadisticas({
      totalEvaluaciones: completadas.length + totalPendientes,
      evaluacionesCompletadas: completadas.length,
      evaluacionesPendientes: totalPendientes,
      autoevaluacionesPendientes: autoevaluacionesPendientes,
      evaluacionesSupervisorPendientes: evaluacionesSupervisorPendientes,
      totalAsignadas: totalAsignadas,
      autoevaluacionesRealizadas: autoevaluacionesRealizadas.length,
      evaluacionesRealizadas: evaluacionesRealizadas.length,
      promedioGeneral: promedioTotal,
      cobertura: cobertura,
      totalEmpleados: totalEmpleados,
      empleadosEvaluados: empleadosEvaluados,
      distribucionPuntajes: distribucionPuntajes
    });
  };

  const handleEvaluacionClick = async (evaluacion) => {
    setSelectedEvaluacion(evaluacion);
    setShowModal(true);
    setCargandoCompetencias(true);
    
    try {
      console.log(`🔄 Cargando competencias para evaluación: ${evaluacion.empleado_nombre}`);
      const competenciasOrdenadas = await getCompetenciasEnOrdenOriginal(evaluacion.empleado_cedula);
      setCompetenciasModal(competenciasOrdenadas);
      console.log(`✅ Competencias cargadas: ${competenciasOrdenadas.length}`);
    } catch (error) {
      console.error('Error cargando competencias:', error);
      setCompetenciasModal([]);
    } finally {
      setCargandoCompetencias(false);
    }
  };

  // Funciones para edición del psicólogo
  const toggleEdicion = (tipo, competenciaId = null) => {
    if (tipo === 'interpretacion') {
      setModoEdicion(prev => ({
        ...prev,
        interpretacion: {
          ...prev.interpretacion,
          [competenciaId]: !prev.interpretacion[competenciaId]
        }
      }));
    } else if (tipo === 'resumenEjecutivo') {
      setModoEdicion(prev => ({
        ...prev,
        resumenEjecutivo: !prev.resumenEjecutivo
      }));
    }
  };

  const handleInterpretacionChange = (competenciaId, nuevaInterpretacion) => {
    setInterpretacionesEditadas(prev => ({
      ...prev,
      [competenciaId]: nuevaInterpretacion
    }));
  };

  const handleResumenEjecutivoChange = (nuevoResumen) => {
    setResumenEjecutivoEditado(nuevoResumen);
  };

  const guardarCambiosPsicologo = async (tipo, competenciaId = null) => {
    setGuardandoCambios(true);
    
    try {
      console.log('💾 Guardando cambios del psicólogo...');
      
      // Buscar el documento en eval180_terminada para actualizar
      const evaluacionesSnapshot = await getDocs(query(
        collection(db, 'eval180_terminada'),
        where('empleado_cedula', '==', selectedEvaluacion.empleado_cedula)
      ));
      
      if (!evaluacionesSnapshot.empty) {
        const docRef = evaluacionesSnapshot.docs[0].ref;
        
        if (tipo === 'interpretacion' && competenciaId) {
          // Guardar interpretación editada
          const interpretacionEditada = interpretacionesEditadas[competenciaId];
          await updateDoc(docRef, {
            [`interpretaciones_psicologo.${competenciaId}`]: interpretacionEditada,
            fecha_ultima_edicion_psicologo: new Date()
          });
          
          console.log(`✅ Interpretación guardada para competencia ${competenciaId}`);
          toggleEdicion('interpretacion', competenciaId);
          
        } else if (tipo === 'resumenEjecutivo') {
          // Guardar resumen ejecutivo editado
          await updateDoc(docRef, {
            resumen_ejecutivo_psicologo: resumenEjecutivoEditado,
            fecha_ultima_edicion_psicologo: new Date()
          });
          
          console.log('✅ Resumen ejecutivo guardado');
          toggleEdicion('resumenEjecutivo');
        }
        
        // Recargar datos para mostrar cambios
        await cargarDatos();
        
        alert('Cambios guardados exitosamente');
      }
    } catch (error) {
      console.error('❌ Error guardando cambios:', error);
      alert('Error al guardar los cambios. Intenta nuevamente.');
    } finally {
      setGuardandoCambios(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedEvaluacion(null);
    setCompetenciasModal([]);
    // Limpiar estados de edición
    setInterpretacionesEditadas({});
    setResumenEjecutivoEditado('');
    setModoEdicion({ interpretacion: {}, resumenEjecutivo: false });
  };

  // Función para generar PDF completo con gráfica de torta
  const handleGenerarPdfClick = async (evaluacion, e) => {
    e.stopPropagation(); // Evitar que se abra el modal
    
    setGenerandoPdf(evaluacion.id);
    
    try {
      console.log('📄 Generando PDF para:', evaluacion.empleado_nombre);
      
      // 1. Obtener los datos completos (igual que en el modal)
      const competenciasData = await getCompetenciasEnOrdenOriginal(evaluacion.empleado_cedula);
      const planDesarrollo = generarPlanDesarrollo(evaluacion, competenciasData);
      
      // 2. Preparar datos para la gráfica de torta
      const autoScore = evaluacion.promedio_autoevaluacion || 0;
      const supScore = evaluacion.promedio_supervisor || 0;
      const total = autoScore + supScore;
      
      const chartData = {
        labels: ['Autoevaluación', 'Evaluación Supervisor'],
        datasets: [{
          data: [autoScore, supScore],
          backgroundColor: ['#3b82f6', '#10b981'],
          borderColor: ['#1d4ed8', '#059669'],
          borderWidth: 2
        }]
      };
      
      // 3. Crear contenido HTML para el PDF
      const htmlContent = crearContenidoPdf(evaluacion, competenciasData, planDesarrollo, chartData);
      
      // 4. Generar PDF usando html2pdf
      const opt = {
        margin: [0.1, 0.5, 0.5, 0.5], // Reducido margen superior de 0.5 a 0.1 (1cm menos aprox)
        filename: `Informe-Evaluacion-${evaluacion.empleado_nombre?.replace(/\s+/g, '-') || 'Empleado'}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          letterRendering: true,
          allowTaint: true
        },
        jsPDF: { 
          unit: 'in', 
          format: 'letter', 
          orientation: 'portrait' 
        }
      };
      
      await html2pdf().set(opt).from(htmlContent).save();
      
      console.log('✅ PDF generado exitosamente');
      
    } catch (error) {
      console.error('❌ Error generando PDF:', error);
      alert('Error al generar el PDF. Por favor intenta nuevamente.');
    } finally {
      setGenerandoPdf(null);
    }
  };

  // Función para crear el contenido HTML del PDF
  const crearContenidoPdf = (evaluacion, competenciasData, planDesarrollo, chartData) => {
    const nombreCompleto = evaluacion.empleado_apellido 
      ? `${evaluacion.empleado_nombre} ${evaluacion.empleado_apellido}`
      : evaluacion.empleado_nombre;
      
    const fechaEvaluacion = (() => {
      const fecha = evaluacion.fecha_procesamiento;
      if (!fecha) return 'No especificado';
      
      let fechaObj;
      if (fecha.seconds) {
        fechaObj = new Date(fecha.seconds * 1000);
      } else if (fecha.toDate) {
        fechaObj = fecha.toDate();
      } else {
        fechaObj = new Date(fecha);
      }
      
      if (isNaN(fechaObj.getTime())) return 'Fecha no válida';
      
      return fechaObj.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long', 
        day: 'numeric'
      });
    })();

    // Crear canvas para la gráfica de torta
    const canvas = document.createElement('canvas');
    canvas.width = 300;
    canvas.height = 300;
    const ctx = canvas.getContext('2d');
    
    // Dibujar gráfica de torta manualmente (ya que Chart.js requiere DOM)
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = 100;
    
    const autoScore = chartData.datasets[0].data[0];
    const supScore = chartData.datasets[0].data[1];
    const total = autoScore + supScore;
    
    if (total > 0) {
      const autoAngle = (autoScore / total) * 2 * Math.PI;
      const supAngle = (supScore / total) * 2 * Math.PI;
      
      // Dibujar sector de autoevaluación
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, radius, 0, autoAngle);
      ctx.closePath();
      ctx.fillStyle = '#3b82f6';
      ctx.fill();
      
      // Dibujar sector de supervisor
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, radius, autoAngle, autoAngle + supAngle);
      ctx.closePath();
      ctx.fillStyle = '#10b981';
      ctx.fill();
      
      // Añadir bordes
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 3;
      ctx.stroke();
    }
    
    const chartImageUrl = canvas.toDataURL('image/png');

    return `
      <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; color: #333; font-size: 12px; line-height: 1.2;">
        <!-- Encabezado -->
        <div style="text-align: center; margin-bottom: 20px; border-bottom: 3px solid #3b82f6; padding-bottom: 15px;">
          <h1 style="color: #1e40af; margin: 0; font-size: 18px;">INFORME DE EVALUACIÓN 180°</h1>
          <h2 style="color: #64748b; margin: 5px 0 0 0; font-weight: normal; font-size: 14px;">Sistema de Evaluación de Competencias</h2>
          <p style="margin: 3px 0 0 0; color: #94a3b8; font-size: 12px;">Fecha de generación: ${new Date().toLocaleDateString('es-ES')}</p>
        </div>

        <!-- Información del Empleado -->
        <div style="background: #f8fafc; padding: 15px; border-radius: 6px; margin-bottom: 15px; border-left: 4px solid #3b82f6;">
          <h3 style="color: #1e40af; margin: 0 0 10px 0; font-size: 14px;">INFORMACIÓN DEL EMPLEADO</h3>
          <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
            <tr>
              <td style="padding: 4px 0; width: 30%; font-weight: bold; color: #475569;">Nombre Completo:</td>
              <td style="padding: 4px 0; color: #1e293b;">${nombreCompleto}</td>
            </tr>
            <tr>
              <td style="padding: 4px 0; font-weight: bold; color: #475569;">Cargo:</td>
              <td style="padding: 4px 0; color: #1e293b;">${evaluacion.empleado_cargo || 'No especificado'}</td>
            </tr>
            <tr>
              <td style="padding: 4px 0; font-weight: bold; color: #475569;">Cédula:</td>
              <td style="padding: 4px 0; color: #1e293b;">${evaluacion.empleado_cedula || 'No especificado'}</td>
            </tr>
            <tr>
              <td style="padding: 4px 0; font-weight: bold; color: #475569;">Supervisor:</td>
              <td style="padding: 4px 0; color: #1e293b;">${evaluacion.supervisor_nombre || 'No especificado'} ${evaluacion.supervisor_apellido || ''}</td>
            </tr>
            <tr>
              <td style="padding: 4px 0; font-weight: bold; color: #475569;">Fecha de Evaluación:</td>
              <td style="padding: 4px 0; color: #1e293b;">${fechaEvaluacion}</td>
            </tr>
          </table>
        </div>

        <!-- Resultados Generales con Gráfica -->
        <div style="background: #ffffff; padding: 15px; border-radius: 6px; margin-bottom: 15px; border: 1px solid #e2e8f0;">
          <h3 style="color: #1e40af; margin: 0 0 10px 0; font-size: 14px;">RESULTADOS GENERALES</h3>
          
          <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px;">
            <div style="flex: 1;">
              <div style="margin-bottom: 8px;">
                <span style="font-weight: bold; color: #475569; font-size: 12px;">🎯 Ponderado Final:</span>
                <span style="font-size: 16px; font-weight: bold; color: #1e40af; margin-left: 8px;">
                  ${evaluacion.ponderado_final ? evaluacion.ponderado_final.toFixed(2) : 'N/A'}
                </span>
              </div>
              <div style="margin-bottom: 5px;">
                <span style="font-weight: bold; color: #475569; font-size: 12px;">👤 Autoevaluación:</span>
                <span style="font-size: 14px; color: #3b82f6; margin-left: 8px;">
                  ${evaluacion.promedio_autoevaluacion ? evaluacion.promedio_autoevaluacion.toFixed(2) : 'N/A'}
                </span>
              </div>
              <div style="margin-bottom: 5px;">
                <span style="font-weight: bold; color: #475569; font-size: 12px;">👨‍💼 Supervisor:</span>
                <span style="font-size: 14px; color: #10b981; margin-left: 8px;">
                  ${evaluacion.promedio_supervisor ? evaluacion.promedio_supervisor.toFixed(2) : 'N/A'}
                </span>
              </div>
              ${evaluacion.promedio_autoevaluacion && evaluacion.promedio_supervisor ? `
              <div>
                <span style="font-weight: bold; color: #475569; font-size: 12px;">📊 Diferencia:</span>
                <span style="font-size: 12px; color: ${Math.abs(evaluacion.promedio_autoevaluacion - evaluacion.promedio_supervisor) > 0.5 ? '#ef4444' : '#6b7280'}; margin-left: 8px;">
                  ${(evaluacion.promedio_autoevaluacion - evaluacion.promedio_supervisor).toFixed(2)}
                </span>
              </div>
              ` : ''}
            </div>
            
            <!-- Gráfica de Torta -->
            <div style="flex: 0 0 auto; text-align: center; margin-left: 20px;">
              <h4 style="margin: 0 0 8px 0; color: #475569; font-size: 12px;">Distribución de Puntuaciones</h4>
              <img src="${chartImageUrl}" style="width: 150px; height: 150px; border-radius: 50%;" alt="Gráfica de distribución">
              <div style="margin-top: 8px; font-size: 10px;">
                <div style="margin: 3px 0;">
                  <span style="display: inline-block; width: 10px; height: 10px; background: #3b82f6; margin-right: 4px;"></span>
                  Autoevaluación (${autoScore.toFixed(1)})
                </div>
                <div style="margin: 3px 0;">
                  <span style="display: inline-block; width: 10px; height: 10px; background: #10b981; margin-right: 4px;"></span>
                  Supervisor (${supScore.toFixed(1)})
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Desglose de Competencias -->
        <div style="background: #ffffff; padding: 15px; border-radius: 6px; margin-bottom: 15px; border: 1px solid #e2e8f0;">
          <h3 style="color: #1e40af; margin: 0 0 10px 0; font-size: 14px;">DESGLOSE DE COMPETENCIAS</h3>
          <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
            <thead>
              <tr style="background: #f1f5f9;">
                <th style="padding: 8px; text-align: left; border: 1px solid #e2e8f0; font-weight: bold; color: #475569;">Competencia</th>
                <th style="padding: 8px; text-align: center; border: 1px solid #e2e8f0; font-weight: bold; color: #475569;">Tipo</th>
                <th style="padding: 8px; text-align: center; border: 1px solid #e2e8f0; font-weight: bold; color: #475569;">Autoevaluación</th>
                <th style="padding: 8px; text-align: center; border: 1px solid #e2e8f0; font-weight: bold; color: #475569;">Supervisor</th>
                <th style="padding: 8px; text-align: center; border: 1px solid #e2e8f0; font-weight: bold; color: #475569;">Diferencia</th>
              </tr>
            </thead>
            <tbody>
              ${competenciasData.map(competencia => {
                const auto = parseFloat(competencia.autoScore);
                const sup = parseFloat(competencia.supScore);
                const diff = !isNaN(auto) && !isNaN(sup) ? (auto - sup).toFixed(2) : 'N/A';
                const diffColor = !isNaN(auto) && !isNaN(sup) && Math.abs(auto - sup) > 0.7 ? '#ef4444' : '#6b7280';
                
                return `
                <tr>
                  <td style="padding: 6px; border: 1px solid #e2e8f0; color: #1e293b;">${competencia.nombre}</td>
                  <td style="padding: 6px; text-align: center; border: 1px solid #e2e8f0;">
                    <span style="background: ${competencia.tipo === 'funcional' ? '#dbeafe' : competencia.tipo === 'comportamental' ? '#dcfce7' : '#fef3c7'}; 
                                 color: ${competencia.tipo === 'funcional' ? '#1e40af' : competencia.tipo === 'comportamental' ? '#166534' : '#92400e'}; 
                                 padding: 2px 6px; border-radius: 3px; font-size: 10px;">
                      ${competencia.tipo}
                    </span>
                  </td>
                  <td style="padding: 6px; text-align: center; border: 1px solid #e2e8f0; color: #3b82f6; font-weight: bold;">
                    ${competencia.autoScore}
                  </td>
                  <td style="padding: 6px; text-align: center; border: 1px solid #e2e8f0; color: #10b981; font-weight: bold;">
                    ${competencia.supScore}
                  </td>
                  <td style="padding: 6px; text-align: center; border: 1px solid #e2e8f0; color: ${diffColor}; font-weight: bold;">
                    ${diff}
                  </td>
                </tr>
                `;
              }).join('')}
            </tbody>
          </table>
        </div>
        
        <!-- Salto de página después del desglose de competencias -->
        <div style="page-break-before: always;"></div>

        <!-- Análisis de Competencias Críticas -->
        ${competenciasData.filter(c => c.analisisCritico?.esCritica).length > 0 ? `
        <div style="background: #fef2f2; padding: 15px; border-radius: 6px; margin-bottom: 15px; border-left: 4px solid #ef4444;">
          <h3 style="color: #dc2626; margin: 0 0 10px 0; font-size: 14px;">⚠️ COMPETENCIAS CRÍTICAS</h3>
          ${competenciasData.filter(c => c.analisisCritico?.esCritica).map(competencia => `
            <div style="background: #ffffff; padding: 10px; border-radius: 4px; margin-bottom: 8px; border: 1px solid #fecaca;">
              <h4 style="color: #dc2626; margin: 0 0 5px 0; font-size: 12px;">${competencia.nombre}</h4>
              <p style="margin: 0 0 5px 0; font-size: 12px; color: #7f1d1d;">
                <strong>Nivel de Criticidad:</strong> ${competencia.analisisCritico.nivelCriticidad}
              </p>
              <p style="margin: 0 0 5px 0; font-size: 12px; color: #7f1d1d;">
                <strong>Tipo de Discrepancia:</strong> ${competencia.analisisCritico.tipoDiscrepancia}
              </p>
              <div style="font-size: 12px; color: #991b1b; line-height: 1.2;">
                ${(evaluacion.interpretaciones_psicologo?.[competencia.id] || competencia.analisisCritico.interpretacion).replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br>')}
              </div>
            </div>
          `).join('')}
        </div>
        ` : ''}

        <!-- Plan de Desarrollo -->
        <div style="background: #f0f9ff; padding: 15px; border-radius: 6px; margin-bottom: 15px; border-left: 4px solid #0ea5e9;">
          <h3 style="color: #0369a1; margin: 0 0 10px 0; font-size: 14px;">📋 PLAN DE DESARROLLO</h3>
          
          <div style="margin-bottom: 10px;">
            <h4 style="color: #0369a1; margin: 0 0 5px 0; font-size: 12px;">Resumen Ejecutivo</h4>
            <div style="background: #ffffff; padding: 10px; border-radius: 4px; font-size: 12px; line-height: 1.2; color: #334155;">
              ${(evaluacion.resumen_ejecutivo_psicologo || planDesarrollo.resumenEjecutivo).replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br>')}
            </div>
          </div>

          <div style="margin-bottom: 10px;">
            <h4 style="color: #0369a1; margin: 0 0 5px 0; font-size: 12px;">Objetivos Específicos</h4>
            <div style="background: #ffffff; padding: 10px; border-radius: 4px;">
              ${planDesarrollo.objetivosEspecificos.map(objetivo => `
                <div style="margin-bottom: 5px; padding: 5px; background: #f8fafc; border-left: 3px solid #0ea5e9; font-size: 12px;">
                  ${objetivo.replace(/🎯\s*\*\*(.*?)\*\*/, '<strong>🎯 $1</strong>')}
                </div>
              `).join('')}
            </div>
          </div>

          <div>
            <h4 style="color: #0369a1; margin: 0 0 5px 0; font-size: 12px;">Acciones de Desarrollo</h4>
            <div style="background: #ffffff; padding: 10px; border-radius: 4px;">
              ${planDesarrollo.accionesDesarrollo.map(accion => `
                <div style="margin-bottom: 4px; padding: 3px; font-size: 12px; color: #475569;">
                  • ${accion}
                </div>
              `).join('')}
            </div>
          </div>
        </div>

        <!-- Pie de página con sección de firma del psicólogo -->
        <div style="margin-top: 20px; padding-top: 15px; border-top: 2px solid #e5e7eb;">
          <!-- Sección de Firma del Psicólogo -->
          <div style="margin-bottom: 15px;">
            <h3 style="color: #1e40af; margin: 0 0 10px 0; font-size: 12px;">📝 VALIDACIÓN PROFESIONAL</h3>
            
            <div style="display: flex; justify-content: space-between; align-items: start; margin-bottom: 15px;">
              <div style="width: 60%;">
                <p style="margin: 0 0 4px 0; font-size: 12px; color: #374151;"><strong>Psicólogo Responsable:</strong></p>
                <p style="margin: 0 0 4px 0; font-size: 12px; color: #6b7280;">${datosUsuario?.nombreCompleto || 'Psicólogo'}</p>
                <p style="margin: 0 0 4px 0; font-size: 12px; color: #6b7280;"><strong>Cédula:</strong> ${datosUsuario?.cedula || 'Sin cédula'}</p>
                <p style="margin: 0 0 4px 0; font-size: 12px; color: #6b7280;"><strong>${datosUsuario?.tp || 'TP:'}</strong></p>
              </div>
              
              <div style="width: 35%; text-align: center;">
                <div style="border-bottom: 1px solid #374151; margin-bottom: 5px; height: 40px; display: flex; align-items: end; justify-content: center;">
                  <span style="font-size: 10px; color: #9ca3af; margin-bottom: 3px;">Firma</span>
                </div>
                <p style="margin: 0; font-size: 10px; color: #6b7280;">Fecha: ${new Date().toLocaleDateString('es-ES')}</p>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div style="text-align: center; color: #9ca3af; font-size: 10px; margin-top: 10px;">
            <p style="margin: 0;">Sistema de Evaluación 180° - Informe generado automáticamente</p>
            <p style="margin: 2px 0 0 0;">© ${new Date().getFullYear()} - Todos los derechos reservados</p>
          </div>
        </div>
      </div>
    `;
  };

  // Función para generar análisis psicológico de competencias críticas
  const analizarCompetenciaCritica = (competencia, autoScore, supScore) => {
    const diferencia = parseFloat(autoScore) - parseFloat(supScore);
    const diferenciaAbs = Math.abs(diferencia);
    
    if (diferenciaAbs < 0.7) {
      return null; // No es crítica
    }

    let interpretacion = '';
    let recomendaciones = [];
    let nivelCriticidad = '';
    let color = '';

    // Determinar nivel de criticidad
    if (diferenciaAbs >= 0.7 && diferenciaAbs < 1.0) {
      nivelCriticidad = 'MODERADA';
      color = '#f59e0b'; // Amarillo
    } else if (diferenciaAbs >= 1.0 && diferenciaAbs < 1.5) {
      nivelCriticidad = 'ALTA';
      color = '#ef4444'; // Rojo
    } else {
      nivelCriticidad = 'CRÍTICA';
      color = '#dc2626'; // Rojo intenso
    }

    // Análisis específico por tipo de competencia y dirección de la diferencia
    const tipoCompetencia = competencia.tipo?.toLowerCase() || 'general';
    const nombreCompetencia = competencia.nombre;

    if (diferencia > 0) {
      // EMPLEADO SE CALIFICA MÁS ALTO (Sobrevaloración)
      
      if (tipoCompetencia === 'comportamental') {
        interpretacion = `**SOBREVALORACIÓN EN COMPETENCIA COMPORTAMENTAL**
        
        El empleado se autoevalúa ${diferencia.toFixed(2)} puntos por encima de su supervisor en "${nombreCompetencia}".
        
        En competencias comportamentales, esta discrepancia sugiere:
        • **Sesgo de percepción social**: El empleado puede no ser consciente de cómo otros perciben su comportamiento
        • **Falta de feedback externo**: Posible carencia de retroalimentación constructiva sobre habilidades interpersonales
        • **Autoengaño defensivo**: Mecanismo psicológico para proteger la autoestima en áreas socio-emocionales
        • **Diferencias culturales**: Interpretación distinta de comportamientos apropiados en el contexto laboral`;

        recomendaciones = [
          'Feedback 360° específico sobre comportamientos observables',
          'Coaching en inteligencia emocional y autoconciencia social',
          'Video-feedback para observar comportamientos propios',
          'Mentoring con compañeros para calibrar expectativas sociales',
          'Sesiones de role-playing para practicar habilidades interpersonales'
        ];

      } else if (tipoCompetencia === 'funcional') {
        interpretacion = `**SOBREVALORACIÓN EN COMPETENCIA FUNCIONAL**
        
        El empleado se autoevalúa ${diferencia.toFixed(2)} puntos por encima de su supervisor en "${nombreCompetencia}".
        
        En competencias funcionales específicas del cargo, esto indica:
        • **Efecto Dunning-Kruger**: Incompetencia inconsciente que genera sobreconfianza
        • **Falta de estándares claros**: Ausencia de criterios objetivos de desempeño técnico
        • **Sesgo de confirmación**: Enfoque selectivo en éxitos ignorando áreas de mejora
        • **Necesidad de actualización técnica**: Posible desfase entre habilidades percibidas y requeridas`;

        recomendaciones = [
          'Evaluación técnica objetiva con criterios específicos medibles',
          'Capacitación técnica especializada en áreas identificadas',
          'Supervisión directa con feedback inmediato en tareas específicas',
          'Establecimiento de KPIs claros y medibles para el cargo',
          'Plan de desarrollo técnico con hitos evaluables'
        ];

      } else {
        // Competencia general
        interpretacion = `**SOBREVALORACIÓN GENERAL**
        
        El empleado se autoevalúa ${diferencia.toFixed(2)} puntos por encima de su supervisor en "${nombreCompetencia}".
        
        Esta discrepancia puede indicar:
        • **Sesgo de autoservicio**: Tendencia a atribuirse méritos desproporcionados
        • **Falta de autoconciencia**: Limitada capacidad de autoevaluación objetiva
        • **Necesidad de calibración**: Diferencias en la comprensión de estándares esperados
        • **Posible sobreconfianza**: Estimación inflada de las propias capacidades`;

        recomendaciones = [
          'Sesiones de feedback estructurado con ejemplos específicos',
          'Establecimiento de criterios de evaluación más claros',
          'Autorreflexión guiada con herramientas de autoconocimiento',
          'Coaching para desarrollar humildad intelectual',
          'Seguimiento regular para monitorear progreso en autoconciencia'
        ];
      }

    } else {
      // SUPERVISOR CALIFICA MÁS ALTO (Subvaloración del empleado)
      
      if (tipoCompetencia === 'comportamental') {
        interpretacion = `**SUBVALORACIÓN EN COMPETENCIA COMPORTAMENTAL**
        
        El empleado se autoevalúa ${Math.abs(diferencia).toFixed(2)} puntos por debajo de su supervisor en "${nombreCompetencia}".
        
        En competencias comportamentales, esta discrepancia sugiere:
        • **Síndrome del impostor**: Dudas sobre legitimidad de habilidades interpersonales
        • **Autocrítica excesiva**: Estándares internos irrealmente altos para comportamiento social
        • **Ansiedad social**: Inseguridad sobre percepción de otros en interacciones laborales
        • **Modestia cultural**: Normas familiares/culturales que desalientan el autoreconocimiento`;

        recomendaciones = [
          'Terapia de validación para reconocer fortalezas interpersonales',
          'Registro de éxitos comportamentales con evidencia específica',
          'Feedback positivo estructurado sobre habilidades sociales',
          'Entrenamiento en asertividad y autoafirmación saludable',
          'Construcción gradual de confianza en contextos sociales laborales'
        ];

      } else if (tipoCompetencia === 'funcional') {
        interpretacion = `**SUBVALORACIÓN EN COMPETENCIA FUNCIONAL**
        
        El empleado se autoevalúa ${Math.abs(diferencia).toFixed(2)} puntos por debajo de su supervisor en "${nombreCompetencia}".
        
        En competencias funcionales específicas del cargo, esto indica:
        • **Perfeccionismo técnico**: Estándares internos excesivamente altos para desempeño técnico
        • **Síndrome del impostor profesional**: Dudas sobre competencia técnica a pesar de evidencia contraria
        • **Falta de reconocimiento de expertise**: Incapacidad para valorar conocimientos especializados propios
        • **Comparación ascendente**: Tendencia a compararse con expertos de nivel superior`;

        recomendaciones = [
          'Documentación sistemática de logros técnicos específicos',
          'Reconocimiento formal de certificaciones y competencias técnicas',
          'Mentoring para calibrar niveles apropiados de expertise',
          'Establecimiento de metas técnicas alcanzables e incrementales',
          'Celebración de hitos técnicos y profesionales específicos'
        ];

      } else {
        // Competencia general
        interpretacion = `**SUBVALORACIÓN GENERAL**
        
        El empleado se autoevalúa ${Math.abs(diferencia).toFixed(2)} puntos por debajo de su supervisor en "${nombreCompetencia}".
        
        Esta discrepancia puede indicar:
        • **Síndrome del impostor**: Dudas generalizadas sobre competencia profesional
        • **Autocrítica excesiva**: Estándares internos irrealistamente altos
        • **Baja autoestima profesional**: Dificultad para reconocer logros y fortalezas
        • **Perfeccionismo destructivo**: Enfoque en deficiencias más que en fortalezas`;

        recomendaciones = [
          'Terapia cognitivo-conductual para mejorar autopercepción',
          'Registro sistemático de logros y retroalimentación positiva',
          'Establecimiento de metas realistas y alcanzables',
          'Coaching en autoaceptación y reconocimiento de fortalezas',
          'Desarrollo de autocompasión y perspectiva equilibrada'
        ];
      }
    }

    return {
      esCritica: true,
      diferencia: diferencia,
      diferenciaAbs: diferenciaAbs,
      nivelCriticidad: nivelCriticidad,
      color: color,
      interpretacion: interpretacion,
      recomendaciones: recomendaciones,
      tipoDiscrepancia: diferencia > 0 ? 'Sobrevaloración' : 'Subvaloración',
      tipoCompetencia: tipoCompetencia
    };
  };

  // Nueva función para cargar las competencias en el MISMO ORDEN que los formularios originales
  const getCompetenciasEnOrdenOriginal = async (empleadoCedula) => {
    console.log(`🔍 Cargando competencias en ORDEN ORIGINAL para empleado: ${empleadoCedula}`);
    
    // 1. Buscar la evaluación asignada específica para este empleado
    const evaluacionAsignada = evaluacionesAsignadas.find(evaluacion => {
      const cedulaMatch = evaluacion.empleadoCedula === empleadoCedula || 
                          evaluacion.empleado_cedula === empleadoCedula ||
                          evaluacion.cedula === empleadoCedula;
      return cedulaMatch;
    });
    
    if (!evaluacionAsignada) {
      console.log(`❌ No se encontró evaluación asignada para empleado ${empleadoCedula}`);
      return [];
    }

    // 2. Obtener información del empleado para conocer su cargo
    const empleadoInfo = empleados.find(emp => 
      emp.cedula === empleadoCedula || 
      emp.empleado_cedula === empleadoCedula ||
      emp.id === empleadoCedula
    );

    const cargoEmpleado = empleadoInfo?.cargo || 
                         evaluacionAsignada.empleadoCargo || 
                         evaluacionAsignada.empleado_cargo || 
                         'Sin cargo';

    console.log(`👤 Cargo del empleado: ${cargoEmpleado}`);

    // 3. Obtener las plantillas de evaluación para entender el orden original
    let uniqueCompetenciaIds = [];
    
    try {
      // Buscar en el catálogo de plantillas por el plantillaId de la evaluación
      if (evaluacionAsignada.plantillaId) {
        const plantillaSnapshot = await getDocs(query(
          collection(db, 'plantillas_evaluacion'), 
          where('id', '==', evaluacionAsignada.plantillaId)
        ));
        
        if (!plantillaSnapshot.empty) {
          const plantillaData = plantillaSnapshot.docs[0].data();
          uniqueCompetenciaIds = [...new Set(plantillaData.competenciasSeleccionadas)] || [];
          console.log(`✅ Orden original de competencias desde plantilla: ${uniqueCompetenciaIds.length} competencias`);
        }
      }
      
      // Fallback: usar el orden de competenciasDetalle si no hay plantilla
      if (uniqueCompetenciaIds.length === 0 && evaluacionAsignada.competenciasDetalle) {
        uniqueCompetenciaIds = Object.keys(evaluacionAsignada.competenciasDetalle);
        console.log(`🔄 Usando orden de competenciasDetalle como fallback: ${uniqueCompetenciaIds.length} competencias`);
      }
    } catch (error) {
      console.error('Error cargando plantilla:', error);
      // Fallback final
      if (evaluacionAsignada.competenciasDetalle) {
        uniqueCompetenciaIds = Object.keys(evaluacionAsignada.competenciasDetalle);
        console.log(`🔄 Usando orden de competenciasDetalle por error: ${uniqueCompetenciaIds.length} competencias`);
      }
    }

    // 4. Cargar autoevaluaciones y evaluaciones del supervisor
    let autoevaluacionData = null;
    let evaluacionSupervisorData = null;

    try {
      // Buscar autoevaluación realizada
      const autoevaluacionSnapshot = await getDocs(query(
        collection(db, 'autoevaluaciones_realizadas'),
        where('empleado_cedula', '==', empleadoCedula)
      ));
      
      if (!autoevaluacionSnapshot.empty) {
        autoevaluacionData = autoevaluacionSnapshot.docs[0].data();
        console.log(`✅ Autoevaluación encontrada para ${empleadoCedula}`);
      }

      // Buscar evaluación del supervisor
      const evaluacionSupSnapshot = await getDocs(query(
        collection(db, 'evaluaciones_realizadas'),
        where('empleado_cedula', '==', empleadoCedula)
      ));
      
      if (!evaluacionSupSnapshot.empty) {
        evaluacionSupervisorData = evaluacionSupSnapshot.docs[0].data();
        console.log(`✅ Evaluación supervisor encontrada para ${empleadoCedula}`);
      }
    } catch (error) {
      console.error('Error cargando evaluaciones realizadas:', error);
    }

    // 5. Procesar competencias en el ORDEN ORIGINAL manteniendo filtros
    const competenciasOrdenadas = [];
    
    uniqueCompetenciaIds.forEach((competenciaId, index) => {
      console.log(`🔄 Procesando competencia ${index + 1}/${uniqueCompetenciaIds.length}: ${competenciaId}`);
      
      // Buscar información completa de la competencia
      const competenciaInfo = competencias.find(comp => comp.id === competenciaId);
      
      if (competenciaInfo) {
        const tipo = competenciaInfo.tipo || competenciaInfo.tipoCompetencia || 'sin_tipo';
        console.log(`📖 Competencia: ${competenciaInfo.nombre} | Tipo: ${tipo}`);
        
        // Aplicar filtro de cargo para competencias funcionales
        let incluirCompetencia = true;
        
        if (tipo.toLowerCase() === 'funcional') {
          const competenciaEsDelCargo = tieneRelacionCargo(competenciaInfo.nombre, cargoEmpleado);
          
          if (!competenciaEsDelCargo) {
            console.log(`⚠️ Competencia funcional "${competenciaInfo.nombre}" NO corresponde al cargo "${cargoEmpleado}" - FILTRADA`);
            incluirCompetencia = false;
          } else {
            console.log(`✅ Competencia funcional "${competenciaInfo.nombre}" SÍ corresponde al cargo "${cargoEmpleado}"`);
          }
        }

        if (incluirCompetencia) {
          // Buscar calificaciones en los datos reales
          let autoScore = 'N/A';
          let supScore = 'N/A';

          // Buscar autoevaluación
          if (autoevaluacionData?.respuestas) {
            // Las respuestas están indexadas por pregunta_id, necesitamos calcular promedio por competencia
            const respuestasAuto = autoevaluacionData.respuestas;
            const preguntasDeEstaCompetencia = Object.keys(respuestasAuto).filter(preguntaId => 
              preguntaId.startsWith(competenciaId)
            );
            
            if (preguntasDeEstaCompetencia.length > 0) {
              const sumaCalificaciones = preguntasDeEstaCompetencia.reduce((suma, preguntaId) => {
                const calificacion = parseFloat(respuestasAuto[preguntaId]?.calificacion || 0);
                return suma + calificacion;
              }, 0);
              autoScore = (sumaCalificaciones / preguntasDeEstaCompetencia.length).toFixed(2);
              console.log(`✅ Autoevaluación calculada: ${autoScore} (${preguntasDeEstaCompetencia.length} preguntas)`);
            }
          }

          // Buscar evaluación supervisor
          if (evaluacionSupervisorData?.respuestas) {
            const respuestasSup = evaluacionSupervisorData.respuestas;
            const preguntasDeEstaCompetencia = Object.keys(respuestasSup).filter(preguntaId => 
              preguntaId.startsWith(competenciaId)
            );
            
            if (preguntasDeEstaCompetencia.length > 0) {
              const sumaCalificaciones = preguntasDeEstaCompetencia.reduce((suma, preguntaId) => {
                const calificacion = parseFloat(respuestasSup[preguntaId]?.calificacion || 0);
                return suma + calificacion;
              }, 0);
              supScore = (sumaCalificaciones / preguntasDeEstaCompetencia.length).toFixed(2);
              console.log(`✅ Evaluación supervisor calculada: ${supScore} (${preguntasDeEstaCompetencia.length} preguntas)`);
            }
          }

          competenciasOrdenadas.push({
            id: competenciaId,
            nombre: competenciaInfo.nombre,
            tipo: tipo,
            orden: index,
            autoScore: autoScore,
            supScore: supScore,
            cargoRelacionado: cargoEmpleado,
            analisisCritico: autoScore !== 'N/A' && supScore !== 'N/A' ? 
              analizarCompetenciaCritica(competenciaInfo, autoScore, supScore) : null
          });
        }
      } else {
        console.log(`❌ No se encontró información para competencia ID: ${competenciaId}`);
      }
    });

    console.log(`🎯 Competencias ordenadas finales para ${empleadoCedula}:`, competenciasOrdenadas.map(c => ({
      orden: c.orden,
      nombre: c.nombre,
      tipo: c.tipo,
      auto: c.autoScore,
      sup: c.supScore
    })));
    
    return competenciasOrdenadas;
  };

  const getCompetenciasAsignadasEmpleado = (empleadoCedula) => {
    console.log(`🔍 Buscando competencias ESPECÍFICAS para empleado: ${empleadoCedula}`);
    console.log(`📊 Total evaluaciones asignadas disponibles: ${evaluacionesAsignadas.length}`);
    console.log(`🎯 Total competencias disponibles: ${competencias.length}`);
    
    // FILTRAR: Buscar la evaluación asignada específica para este empleado
    const evaluacionAsignada = evaluacionesAsignadas.find(evaluacion => {
      // Verificar ambos campos posibles de cédula
      const cedulaMatch = evaluacion.empleadoCedula === empleadoCedula || 
                          evaluacion.empleado_cedula === empleadoCedula ||
                          evaluacion.cedula === empleadoCedula;
      
      console.log(`� Evaluación ID: ${evaluacion.id}`);
      console.log(`   - empleadoCedula: ${evaluacion.empleadoCedula}`);
      console.log(`   - empleado_cedula: ${evaluacion.empleado_cedula}`);
      console.log(`   - cedula: ${evaluacion.cedula}`);
      console.log(`   - ¿Coincide con ${empleadoCedula}?: ${cedulaMatch}`);
      
      return cedulaMatch;
    });
    
    if (!evaluacionAsignada) {
      console.log(`❌ No se encontró evaluación asignada específica para empleado ${empleadoCedula}`);
      console.log(`📋 Evaluaciones disponibles:`, evaluacionesAsignadas.map(e => ({
        id: e.id,
        empleadoCedula: e.empleadoCedula || e.empleado_cedula || e.cedula,
        empleadoNombre: e.empleadoNombre || e.empleado_nombre || 'Sin nombre'
      })));
      return {};
    }

    console.log(`✅ Evaluación asignada encontrada para ${empleadoCedula}:`, {
      id: evaluacionAsignada.id,
      empleado: evaluacionAsignada.empleadoNombre || evaluacionAsignada.empleado_nombre,
      cedula: evaluacionAsignada.empleadoCedula || evaluacionAsignada.empleado_cedula
    });

    // Obtener información del empleado para conocer su cargo
    const empleadoInfo = empleados.find(emp => 
      emp.cedula === empleadoCedula || 
      emp.empleado_cedula === empleadoCedula ||
      emp.id === empleadoCedula
    );

    const cargoEmpleado = empleadoInfo?.cargo || 
                         evaluacionAsignada.empleadoCargo || 
                         evaluacionAsignada.empleado_cargo || 
                         'Sin cargo';

    console.log(`👤 Información del empleado:`, {
      cargo: cargoEmpleado,
      fuente: empleadoInfo ? 'empleados' : 'evaluacionAsignada'
    });

    if (!evaluacionAsignada.competenciasDetalle) {
      console.log(`❌ No hay competenciasDetalle en la evaluación para ${empleadoCedula}`);
      console.log(`📋 Campos disponibles en la evaluación:`, Object.keys(evaluacionAsignada));
      return {};
    }

    const competenciasIds = Object.keys(evaluacionAsignada.competenciasDetalle);
    console.log(`🎯 Competencias asignadas a este empleado (${competenciasIds.length}):`, competenciasIds);

    // Organizar competencias por tipo usando competenciasDetalle específico del empleado
    const competenciasPorTipo = {};
    
    // Iterar sobre las competencias específicas de este empleado
    competenciasIds.forEach(competenciaId => {
      console.log(`🔍 Procesando competencia específica ID: ${competenciaId}`);
      
      // Buscar la competencia completa en el catálogo global
      const competenciaInfo = competencias.find(comp => comp.id === competenciaId);
      
      if (competenciaInfo) {
        const tipo = competenciaInfo.tipo || 'sin_tipo';
        console.log(`📖 Competencia encontrada: ${competenciaInfo.nombre} | Tipo: ${tipo}`);
        
        // FILTRO CRÍTICO: Para competencias funcionales, verificar que correspondan al cargo del empleado
        if (tipo.toLowerCase() === 'funcional') {
          // Verificar si la competencia funcional corresponde al cargo del empleado
          const competenciaEsDelCargo = tieneRelacionCargo(competenciaInfo.nombre, cargoEmpleado);
          
          if (!competenciaEsDelCargo) {
            console.log(`⚠️ Competencia funcional "${competenciaInfo.nombre}" NO corresponde al cargo "${cargoEmpleado}" - FILTRADA`);
            return; // Saltar esta competencia
          } else {
            console.log(`✅ Competencia funcional "${competenciaInfo.nombre}" SÍ corresponde al cargo "${cargoEmpleado}"`);
          }
        }
        
        if (!competenciasPorTipo[tipo]) {
          competenciasPorTipo[tipo] = [];
        }
        
        competenciasPorTipo[tipo].push({
          id: competenciaId,
          nombre: competenciaInfo.nombre || competenciaId,
          tipo: competenciaInfo.tipo,
          preguntas: competenciaInfo.preguntas || [],
          porcentaje: evaluacionAsignada.competenciasDetalle[competenciaId]?.porcentaje || 0,
          cargoRelacionado: cargoEmpleado
        });
      } else {
        console.log(`❌ No se encontró información en catálogo para competencia ID: ${competenciaId}`);
      }
    });

    console.log(`🎯 Competencias FILTRADAS por tipo para ${empleadoCedula} (${cargoEmpleado}):`, 
      Object.entries(competenciasPorTipo).map(([tipo, comps]) => ({
        tipo,
        cantidad: comps.length,
        competencias: comps.map(c => c.nombre)
      }))
    );
    
    return competenciasPorTipo;
  };

  // Función auxiliar para determinar si una competencia funcional está relacionada con el cargo (COINCIDENCIA EXACTA)
  const tieneRelacionCargo = (nombreCompetencia, cargoEmpleado) => {
    console.log(`🔍 Verificando coincidencia EXACTA: "${nombreCompetencia}" vs cargo "${cargoEmpleado}"`);
    
    // Limpiar y normalizar textos para comparación exacta
    const normalizar = (texto) => {
      return texto
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Quitar acentos
        .replace(/[^a-z0-9\s]/g, '') // Quitar caracteres especiales
        .replace(/\s+/g, ' ') // Normalizar espacios
        .trim();
    };
    
    const competenciaNormalizada = normalizar(nombreCompetencia);
    const cargoNormalizado = normalizar(cargoEmpleado);
    
    console.log(`📝 Textos normalizados para comparación exacta:`);
    console.log(`   - Competencia: "${competenciaNormalizada}"`);
    console.log(`   - Cargo: "${cargoNormalizado}"`);
    
    // 1. VERIFICACIÓN DE COINCIDENCIA EXACTA COMPLETA
    if (competenciaNormalizada === cargoNormalizado) {
      console.log(`✅ COINCIDENCIA EXACTA COMPLETA: Los textos son idénticos`);
      return true;
    }
    
    // 2. VERIFICACIÓN DE COINCIDENCIA EXACTA DEL CARGO DENTRO DE LA COMPETENCIA
    if (competenciaNormalizada.includes(cargoNormalizado)) {
      console.log(`✅ COINCIDENCIA EXACTA: El cargo "${cargoNormalizado}" está contenido en la competencia`);
      return true;
    }
    
    // 3. VERIFICACIÓN DE COINCIDENCIA EXACTA DE LA COMPETENCIA DENTRO DEL CARGO
    if (cargoNormalizado.includes(competenciaNormalizada)) {
      console.log(`✅ COINCIDENCIA EXACTA: La competencia "${competenciaNormalizada}" está contenida en el cargo`);
      return true;
    }
    
    // 4. VERIFICACIÓN DE PALABRAS CLAVE EXACTAS DEL CARGO
    const palabrasCargo = cargoNormalizado.split(' ').filter(palabra => palabra.length > 2);
    
    console.log(`🔤 Palabras del cargo para verificación exacta:`, palabrasCargo);
    
    // Verificar si TODAS las palabras significativas del cargo están en la competencia
    const todasPalabrasCargoEnCompetencia = palabrasCargo.every(palabra => 
      competenciaNormalizada.includes(palabra)
    );
    
    if (todasPalabrasCargoEnCompetencia && palabrasCargo.length > 0) {
      console.log(`✅ COINCIDENCIA EXACTA: Todas las palabras del cargo están en la competencia`);
      return true;
    }
    
    // 5. VERIFICACIÓN DE PALABRAS CLAVE EXACTAS DE LA COMPETENCIA
    const palabrasCompetencia = competenciaNormalizada.split(' ').filter(palabra => palabra.length > 2);
    
    console.log(`� Palabras de la competencia para verificación exacta:`, palabrasCompetencia);
    
    // Verificar si TODAS las palabras significativas de la competencia están en el cargo
    const todasPalabrasCompetenciaEnCargo = palabrasCompetencia.every(palabra => 
      cargoNormalizado.includes(palabra)
    );
    
    if (todasPalabrasCompetenciaEnCargo && palabrasCompetencia.length > 0) {
      console.log(`✅ COINCIDENCIA EXACTA: Todas las palabras de la competencia están en el cargo`);
      return true;
    }
    
    // SI NO HAY COINCIDENCIA EXACTA, RECHAZAR
    console.log(`❌ NO HAY COINCIDENCIA EXACTA: La competencia "${nombreCompetencia}" no coincide exactamente con el cargo "${cargoEmpleado}"`);
    return false;
  };

  // Función para generar plan de desarrollo específico basado en resultados reales
  const generarPlanDesarrollo = (empleado, competenciasEvaluadas) => {
    console.log('🎯 Generando plan inteligente para:', empleado.empleado_nombre);
    console.log('📊 Competencias evaluadas:', competenciasEvaluadas.length);
    
    // Análisis inteligente de resultados
    const analisisResultados = analizarResultadosEvaluacion(empleado, competenciasEvaluadas);
    
    // Determinar tipo de empleado y necesidades específicas
    const perfilEmpleado = determinarPerfilEmpleado(empleado, analisisResultados);
    
    // Generar plan personalizado
    return generarPlanPersonalizado(empleado, analisisResultados, perfilEmpleado);
  };

  // Análisis inteligente de los resultados de la evaluación 180°
  const analizarResultadosEvaluacion = (empleado, competenciasEvaluadas) => {
    const analisis = {
      fortalezas: [],
      debilidades: [],
      competenciasCriticas: [],
      brechasSignificativas: [],
      patronesComportamiento: [],
      nivelDesempenoGeneral: 'MEDIO',
      indiceAutoconocimiento: 0,
      recomiendaSeguimiento: false
    };

    let sumaAuto = 0, sumaSup = 0, sumaDiferencias = 0;
    let countValid = 0;

    competenciasEvaluadas.forEach(competencia => {
      const autoScore = parseFloat(competencia.autoScore);
      const supScore = parseFloat(competencia.supScore);
      
      if (!isNaN(autoScore) && !isNaN(supScore)) {
        const diferencia = autoScore - supScore;
        const promedio = (autoScore + supScore) / 2;
        
        sumaAuto += autoScore;
        sumaSup += supScore;
        sumaDiferencias += Math.abs(diferencia);
        countValid++;

        // Identificar fortalezas (puntaje alto y consenso)
        if (promedio >= 4.0 && Math.abs(diferencia) <= 0.5) {
          analisis.fortalezas.push({
            competencia: competencia.nombre,
            tipo: competencia.tipo,
            puntaje: promedio,
            consenso: true,
            observacion: `Fortaleza consolidada con alto consenso (Auto: ${autoScore}, Sup: ${supScore})`
          });
        }

        // Identificar debilidades (puntaje bajo y consenso)
        if (promedio <= 2.5 && Math.abs(diferencia) <= 0.5) {
          analisis.debilidades.push({
            competencia: competencia.nombre,
            tipo: competencia.tipo,
            puntaje: promedio,
            consenso: true,
            observacion: `Área crítica de mejora con consenso (Auto: ${autoScore}, Sup: ${supScore})`
          });
        }

        // Competencias críticas (diferencias significativas)
        if (Math.abs(diferencia) >= 0.7) {
          analisis.competenciasCriticas.push({
            competencia: competencia.nombre,
            tipo: competencia.tipo,
            diferencia: diferencia,
            tipoBrecha: diferencia > 0 ? 'SOBREVALORACIÓN' : 'SUBVALORACIÓN',
            observacion: diferencia > 0 
              ? `Empleado se percibe ${diferencia.toFixed(1)} puntos más alto que supervisor`
              : `Empleado se subestima en ${Math.abs(diferencia).toFixed(1)} puntos vs supervisor`
          });
        }

        // Brechas significativas para desarrollo
        if (promedio >= 2.5 && promedio < 4.0) {
          analisis.brechasSignificativas.push({
            competencia: competencia.nombre,
            tipo: competencia.tipo,
            puntaje: promedio,
            potencial: 'ALTO',
            observacion: `Competencia con potencial de desarrollo (puntaje actual: ${promedio.toFixed(1)})`
          });
        }
      }
    });

    // Calcular métricas generales
    if (countValid > 0) {
      const promedioAuto = sumaAuto / countValid;
      const promedioSup = sumaSup / countValid;
      const promedioGeneral = (promedioAuto + promedioSup) / 2;
      const indiceAutoconocimiento = 1 - (sumaDiferencias / countValid / 5); // Normalizado a 0-1

      analisis.indiceAutoconocimiento = Math.max(0, indiceAutoconocimiento);
      
      // Determinar nivel de desempeño general
      if (promedioGeneral >= 4.0) {
        analisis.nivelDesempenoGeneral = 'ALTO';
      } else if (promedioGeneral >= 3.0) {
        analisis.nivelDesempenoGeneral = 'MEDIO';
      } else {
        analisis.nivelDesempenoGeneral = 'BAJO';
      }

      // Patrones de comportamiento
      if (promedioAuto > promedioSup + 0.3) {
        analisis.patronesComportamiento.push('TENDENCIA_SOBREVALORACIÓN');
      } else if (promedioSup > promedioAuto + 0.3) {
        analisis.patronesComportamiento.push('TENDENCIA_SUBVALORACIÓN');
      } else {
        analisis.patronesComportamiento.push('AUTOPERCEPCIÓN_BALANCEADA');
      }

      // Recomendar seguimiento si hay muchas competencias críticas
      analisis.recomiendaSeguimiento = analisis.competenciasCriticas.length >= 2;
    }

    console.log('📈 Análisis completado:', {
      fortalezas: analisis.fortalezas.length,
      debilidades: analisis.debilidades.length,
      criticas: analisis.competenciasCriticas.length,
      nivel: analisis.nivelDesempenoGeneral,
      autoconocimiento: (analisis.indiceAutoconocimiento * 100).toFixed(1) + '%'
    });

    return analisis;
  };

  // Determinar perfil del empleado para personalización
  const determinarPerfilEmpleado = (empleado, analisisResultados) => {
    const cargo = empleado.empleado_cargo?.toLowerCase() || '';
    
    const perfil = {
      esLider: false,
      nivelJerarquico: 'OPERATIVO',
      experienciaAsumida: 'MEDIA',
      tipoDesarrollo: 'TÉCNICO',
      prioridades: []
    };

    // Determinar si es líder
    const cargosLiderazgo = ['director', 'gerente', 'jefe', 'coordinador', 'supervisor', 'líder'];
    perfil.esLider = cargosLiderazgo.some(cargoLider => cargo.includes(cargoLider));

    // Nivel jerárquico
    if (cargo.includes('vicepresidente') || cargo.includes('director')) {
      perfil.nivelJerarquico = 'ALTO';
    } else if (cargo.includes('gerente') || cargo.includes('jefe')) {
      perfil.nivelJerarquico = 'MEDIO';
    } else if (cargo.includes('coordinador') || cargo.includes('supervisor')) {
      perfil.nivelJerarquico = 'BAJO_LIDERAZGO';
    }

    // Tipo de desarrollo basado en resultados
    if (perfil.esLider) {
      perfil.tipoDesarrollo = analisisResultados.competenciasCriticas.length > 2 ? 'LIDERAZGO_INTENSIVO' : 'LIDERAZGO_ESTÁNDAR';
    } else {
      perfil.tipoDesarrollo = analisisResultados.debilidades.length > 1 ? 'TÉCNICO_INTENSIVO' : 'TÉCNICO_ESTÁNDAR';
    }

    // Prioridades basadas en análisis
    if (analisisResultados.competenciasCriticas.length > 0) {
      perfil.prioridades.push('AUTOCONOCIMIENTO');
    }
    if (analisisResultados.debilidades.length > 0) {
      perfil.prioridades.push('COMPETENCIAS_BÁSICAS');
    }
    if (analisisResultados.brechasSignificativas.length > 2) {
      perfil.prioridades.push('DESARROLLO_COMPETENCIAS');
    }
    if (perfil.esLider && analisisResultados.nivelDesempenoGeneral === 'ALTO') {
      perfil.prioridades.push('LIDERAZGO_AVANZADO');
    }

    return perfil;
  };

  // Generar plan personalizado basado en análisis
  const generarPlanPersonalizado = (empleado, analisisResultados, perfilEmpleado) => {
    const plan = {
      tipoEmpleado: perfilEmpleado.esLider ? 'LÍDER' : 'INDIVIDUAL',
      nivelDesempeño: analisisResultados.nivelDesempenoGeneral,
      duracion: determinarDuracionPlan(analisisResultados, perfilEmpleado),
      prioridadDesarrollo: determinarPrioridad(analisisResultados),
      
      resumenEjecutivo: generarResumenEjecutivo(empleado, analisisResultados, perfilEmpleado),
      objetivosEspecificos: generarObjetivosEspecificos(analisisResultados, perfilEmpleado),
      accionesDesarrollo: generarAccionesDesarrollo(analisisResultados, perfilEmpleado),
      metasEvaluables: generarMetasEvaluables(analisisResultados, perfilEmpleado),
      cronogramaMensual: generarCronogramaMensual(analisisResultados, perfilEmpleado),
      seguimientoRequerido: analisisResultados.recomiendaSeguimiento
    };

    return plan;
  };

  // Funciones auxiliares para generar contenido específico
  const determinarDuracionPlan = (analisis, perfil) => {
    if (analisis.competenciasCriticas.length >= 3) return '6 meses';
    if (analisis.debilidades.length >= 2) return '5 meses';
    if (perfil.esLider && analisis.nivelDesempenoGeneral === 'BAJO') return '6 meses';
    return '4 meses';
  };

  const determinarPrioridad = (analisis) => {
    if (analisis.debilidades.length > 1) return 'ALTA';
    if (analisis.competenciasCriticas.length > 2) return 'ALTA';
    if (analisis.nivelDesempenoGeneral === 'BAJO') return 'ALTA';
    return 'MEDIA';
  };

  const generarResumenEjecutivo = (empleado, analisis, perfil) => {
    const nombreCompleto = empleado.empleado_apellido 
      ? `${empleado.empleado_nombre} ${empleado.empleado_apellido}`
      : empleado.empleado_nombre;
    const cargo = empleado.empleado_cargo;
    
    let resumen = `**${nombreCompleto}** (${cargo}) presenta un desempeño **${analisis.nivelDesempenoGeneral}** `;
    resumen += `con un índice de autoconocimiento del **${(analisis.indiceAutoconocimiento * 100).toFixed(0)}%**.\n\n`;
    
    if (analisis.fortalezas.length > 0) {
      resumen += `**Fortalezas identificadas:** ${analisis.fortalezas.map(f => f.competencia).join(', ')}.\n\n`;
    }
    
    if (analisis.debilidades.length > 0) {
      resumen += `**Áreas críticas de mejora:** ${analisis.debilidades.map(d => d.competencia).join(', ')}.\n\n`;
    }
    
    if (analisis.competenciasCriticas.length > 0) {
      resumen += `**Competencias que requieren alineación:** ${analisis.competenciasCriticas.length} competencia(s) `;
      resumen += `muestran diferencias significativas entre autoevaluación y evaluación supervisorial.\n\n`;
    }
    
    resumen += `**Enfoque del plan:** ${perfil.tipoDesarrollo.replace('_', ' ').toLowerCase()} `;
    resumen += `con prioridad **${determinarPrioridad(analisis)}**.`;
    
    return resumen;
  };

  const generarObjetivosEspecificos = (analisis, perfil) => {
    const objetivos = [];
    
    // Objetivos basados en debilidades críticas
    analisis.debilidades.forEach(debilidad => {
      objetivos.push(
        `🎯 **Fortalecer ${debilidad.competencia}**: Elevar competencia desde ${debilidad.puntaje.toFixed(1)} a mínimo 3.5 puntos en ${perfil.esLider ? '4 meses' : '3 meses'}`
      );
    });

    // Objetivos para competencias críticas (diferencias de percepción)
    analisis.competenciasCriticas.forEach(critica => {
      if (critica.tipoBrecha === 'SOBREVALORACIÓN') {
        objetivos.push(
          `🔍 **Calibrar autopercepción en ${critica.competencia}**: Desarrollar autoconciencia y alinear percepción con estándares supervisoriales`
        );
      } else {
        objetivos.push(
          `💪 **Reconocer fortalezas en ${critica.competencia}**: Desarrollar confianza y validar competencias demostradas`
        );
      }
    });

    // Objetivos para brechas de desarrollo
    const brechasTop = analisis.brechasSignificativas.slice(0, 2);
    brechasTop.forEach(brecha => {
      objetivos.push(
        `📈 **Desarrollar ${brecha.competencia}**: Avanzar desde ${brecha.puntaje.toFixed(1)} hacia 4.0+ puntos mediante práctica dirigida`
      );
    });

    // Objetivo de liderazgo si aplica
    if (perfil.esLider && analisis.nivelDesempenoGeneral !== 'BAJO') {
      objetivos.push(
        `👥 **Optimizar impacto de liderazgo**: Potenciar fortalezas identificadas para maximizar resultados del equipo`
      );
    }

    return objetivos.length > 0 ? objetivos : [
      `🎯 **Mantener nivel de desempeño**: Consolidar competencias actuales y explorar oportunidades de crecimiento`
    ];
  };

  const generarAccionesDesarrollo = (analisis, perfil) => {
    const acciones = [];
    
    // Acciones para debilidades críticas
    analisis.debilidades.forEach(debilidad => {
      if (debilidad.tipo === 'comportamental') {
        acciones.push(`📚 Capacitación en ${debilidad.competencia}: 2 sesiones de 4 horas con práctica supervisada`);
        acciones.push(`🎭 Role-playing mensual para aplicar ${debilidad.competencia} en situaciones reales`);
      } else if (debilidad.tipo === 'funcional') {
        acciones.push(`🔧 Entrenamiento técnico específico en ${debilidad.competencia}: 40 horas prácticas`);
        acciones.push(`👨‍🏫 Mentoring con experto interno en ${debilidad.competencia} (sesiones quincenales)`);
      } else {
        acciones.push(`📖 Desarrollo de ${debilidad.competencia}: Plan estructurado de 6 semanas`);
      }
    });

    // Acciones para competencias críticas
    analisis.competenciasCriticas.forEach(critica => {
      if (critica.tipoBrecha === 'SOBREVALORACIÓN') {
        acciones.push(`🎯 Feedback 360° específico sobre ${critica.competencia} con ejemplos concretos`);
        acciones.push(`📹 Video-análisis de desempeño en ${critica.competencia} para auto-observación`);
      } else {
        acciones.push(`🏆 Sesiones de reconocimiento y validación de logros en ${critica.competencia}`);
        acciones.push(`📋 Documentación semanal de éxitos específicos en ${critica.competencia}`);
      }
    });

    // Acciones para desarrollo general
    if (analisis.brechasSignificativas.length > 0) {
      const brechaTop = analisis.brechasSignificativas[0];
      acciones.push(`🎯 Proyecto especial enfocado en ${brechaTop.competencia} con seguimiento quincenal`);
    }

    // Acciones específicas para líderes
    if (perfil.esLider) {
      acciones.push(`👥 Coaching de liderazgo personalizado (2 horas mensuales)`);
      if (analisis.nivelDesempenoGeneral === 'ALTO') {
        acciones.push(`🌟 Participación en proyecto de desarrollo organizacional`);
      }
    }

    return acciones.length > 0 ? acciones : [
      `📈 Plan de mantenimiento: Revisión mensual de competencias actuales`,
      `🎯 Exploración de nuevas oportunidades de desarrollo`
    ];
  };

  const generarMetasEvaluables = (analisis, perfil) => {
    const metas = [];
    
    // Metas específicas por competencias críticas
    analisis.debilidades.forEach((debilidad, index) => {
      metas.push(
        `**Mes ${index + 2}**: Evaluación de progreso en ${debilidad.competencia} - Meta: ≥3.0 puntos en autoevaluación y evaluación supervisorial`
      );
    });

    // Metas para competencias críticas
    if (analisis.competenciasCriticas.length > 0) {
      metas.push(
        `**Mes 3**: Reducir diferencias de percepción a <0.5 puntos en al menos 60% de competencias críticas identificadas`
      );
    }

    // Meta de desarrollo general
    if (analisis.brechasSignificativas.length > 0) {
      metas.push(
        `**Mes 4**: Incrementar promedio general en 0.3+ puntos respecto a evaluación inicial`
      );
    }

    // Meta final
    const metaFinal = perfil.esLider 
      ? `**Evaluación final**: Demostrar mejora sostenida en indicadores de liderazgo y impacto en equipo`
      : `**Evaluación final**: Alcanzar nivel de competencia requerido para el rol actual`;
    
    metas.push(metaFinal);

    return metas;
  };

  const generarCronogramaMensual = (analisis, perfil) => {
    const duracion = parseInt(determinarDuracionPlan(analisis, perfil).split(' ')[0]);
    const cronograma = [];
    
    // Mes 1: Establecimiento y planificación
    cronograma.push(
      `**Mes 1**: Sesión de alineación inicial, establecimiento de línea base y inicio de acciones prioritarias`
    );

    // Meses intermedios basados en análisis
    for (let mes = 2; mes < duracion; mes++) {
      if (analisis.debilidades.length > 0 && mes === 2) {
        cronograma.push(
          `**Mes ${mes}**: Enfoque intensivo en competencias críticas identificadas como debilidades`
        );
      } else if (analisis.competenciasCriticas.length > 0 && mes === 3) {
        cronograma.push(
          `**Mes ${mes}**: Trabajo en alineación de percepciones y desarrollo de autoconocimiento`
        );
      } else {
        cronograma.push(
          `**Mes ${mes}**: Aplicación práctica y consolidación de competencias en desarrollo`
        );
      }
    }

    // Mes final
    cronograma.push(
      `**Mes ${duracion}**: Evaluación final, validación de logros y planificación de seguimiento`
    );

    return cronograma;
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingCard}>
          <div style={styles.spinner}></div>
          <p style={styles.loadingText}>Cargando Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          .plan-item::before {
            content: "✓";
            position: absolute;
            left: 0;
            color: #059669;
            font-weight: bold;
          }
          
          .plan-item-critico::before {
            content: "🚨";
            position: absolute;
            left: 0;
          }
          
          .cronograma-item::before {
            content: "📅";
            position: absolute;
            left: 0.25rem;
          }
          
          .meta-item::before {
            content: "🎯";
            position: absolute;
            left: 0.25rem;
          }
        `}
      </style>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.headerLeft}>
            <h1 style={styles.title}>Dashboard Psicólogo</h1>
            <p style={styles.subtitle}>
              Bienvenido, {user?.email || 'Psicólogo'}
            </p>
          </div>
          <div style={styles.headerActions}>
            <button 
              onClick={handleLogout}
              style={styles.logoutButton}
            >
              Cerrar Sesión
            </button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav style={styles.navigation}>
        <div style={styles.tabContainer}>
          {[
            { id: 'overview', label: 'Resumen General' },
            { id: 'evaluaciones', label: 'Evaluaciones' },
            { id: 'analisis', label: 'Análisis Psicológico' },
            { id: 'reportes', label: 'Reportes' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                ...styles.tab,
                ...(activeTab === tab.id ? styles.activeTab : {})
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </nav>

      {/* Main Content */}
      <main style={styles.main}>
        {activeTab === 'overview' && (
          <div style={styles.overviewContainer}>
            <div style={styles.statsGrid}>
              <div style={styles.statCard}>
                <h3 style={styles.statTitle}>Evaluaciones Completadas</h3>
                <p style={styles.statValue}>{estadisticas.evaluacionesCompletadas}</p>
                <p style={styles.statSubtitle}>de {estadisticas.totalEvaluaciones} totales</p>
              </div>
              <div style={styles.statCard}>
                <h3 style={styles.statTitle}>Evaluaciones Pendientes</h3>
                <p style={styles.statValue}>{estadisticas.evaluacionesPendientes}</p>
                <div style={styles.statBreakdown}>
                  <p style={styles.statSubtitle}>
                    👤 {estadisticas.autoevaluacionesPendientes || 0} autoevaluaciones
                  </p>
                  <p style={styles.statSubtitle}>
                    👨‍💼 {estadisticas.evaluacionesSupervisorPendientes || 0} eval. supervisor
                  </p>
                </div>
              </div>
              <div style={styles.statCard}>
                <h3 style={styles.statTitle}>Promedio General</h3>
                <p style={styles.statValue}>{estadisticas.promedioGeneral?.toFixed(1) || '0.0'}</p>
                <p style={styles.statSubtitle}>ponderado final</p>
              </div>
              <div style={styles.statCard}>
                <h3 style={styles.statTitle}>Cobertura</h3>
                <p style={styles.statValue}>{estadisticas.cobertura?.toFixed(1) || '0.0'}%</p>
                <p style={styles.statSubtitle}>{estadisticas.empleadosEvaluados || 0} de {estadisticas.totalEmpleados || 0} empleados</p>
              </div>
            </div>
            
            {/* Distribución de Puntajes */}
            {estadisticas.distribucionPuntajes && (
              <div style={styles.distributionContainer}>
                <h3 style={styles.sectionTitle}>Distribución de Desempeño</h3>
                <div style={styles.distributionGrid}>
                  <div style={{...styles.distributionCard, backgroundColor: '#10b981'}}>
                    <h4 style={styles.distributionTitle}>Excelente (4.5-5.0)</h4>
                    <p style={styles.distributionValue}>{estadisticas.distribucionPuntajes.excelente}</p>
                  </div>
                  <div style={{...styles.distributionCard, backgroundColor: '#3b82f6'}}>
                    <h4 style={styles.distributionTitle}>Bueno (3.5-4.4)</h4>
                    <p style={styles.distributionValue}>{estadisticas.distribucionPuntajes.bueno}</p>
                  </div>
                  <div style={{...styles.distributionCard, backgroundColor: '#f59e0b'}}>
                    <h4 style={styles.distributionTitle}>Regular (2.5-3.4)</h4>
                    <p style={styles.distributionValue}>{estadisticas.distribucionPuntajes.regular}</p>
                  </div>
                  <div style={{...styles.distributionCard, backgroundColor: '#ef4444'}}>
                    <h4 style={styles.distributionTitle}>Deficiente (&lt;2.5)</h4>
                    <p style={styles.distributionValue}>{estadisticas.distribucionPuntajes.deficiente}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'evaluaciones' && (
          <div style={styles.evaluacionesContainer}>
            <h2 style={styles.sectionTitle}>
              Evaluaciones 180° Completadas ({evaluaciones.length} evaluaciones)
            </h2>
            <div style={styles.evaluacionesList}>
              {evaluaciones.length > 0 ? (
                evaluaciones.map(evaluacion => (
                  <div 
                    key={evaluacion.id} 
                    style={{...styles.evaluacionItem, cursor: 'pointer'}}
                    onClick={() => handleEvaluacionClick(evaluacion)}
                  >
                    <div style={styles.evaluacionInfo}>
                      <h4 style={styles.evaluacionTitulo}>
                        {evaluacion.empleado_nombre || 'Empleado'} {evaluacion.empleado_apellido || ''}
                      </h4>
                      <p style={styles.evaluacionDetalle}>
                        <strong>Cargo:</strong> {evaluacion.empleado_cargo || 'No especificado'}
                      </p>
                      <p style={styles.evaluacionDetalle}>
                        <strong>Cédula:</strong> {evaluacion.empleado_cedula || 'No especificado'}
                      </p>
                      <p style={styles.evaluacionDetalle}>
                        <strong>Supervisor:</strong> {evaluacion.supervisor_nombre || 'No especificado'} {evaluacion.supervisor_apellido || ''}
                      </p>
                      <p style={styles.evaluacionDetalle}>
                        <strong>Fecha de ponderación:</strong> {(() => {
                          const fecha = evaluacion.fecha_procesamiento;
                          if (!fecha) return 'No especificado';
                          
                          // Manejar diferentes formatos de fecha
                          let fechaObj;
                          if (fecha.seconds) {
                            // Firestore Timestamp
                            fechaObj = new Date(fecha.seconds * 1000);
                          } else if (fecha.toDate) {
                            // Firestore Timestamp con método toDate()
                            fechaObj = fecha.toDate();
                          } else if (typeof fecha === 'string' || typeof fecha === 'number') {
                            // String o número
                            fechaObj = new Date(fecha);
                          } else {
                            return 'Formato de fecha no válido';
                          }
                          
                          // Verificar si la fecha es válida
                          if (isNaN(fechaObj.getTime())) {
                            return 'Fecha no válida';
                          }
                          
                          return fechaObj.toLocaleDateString('es-ES', {
                            year: 'numeric',
                            month: 'long', 
                            day: 'numeric'
                          });
                        })()}
                      </p>
                      <span style={{
                        ...styles.badge,
                        backgroundColor: '#10b981'
                      }}>
                        ✅ Evaluación Completa
                      </span>
                    </div>
                    <div style={styles.evaluacionScore}>
                      <div style={styles.scoreDetails}>
                        <div style={styles.scoreItem}>
                          <span style={styles.scoreLabel}>🎯 Ponderado Final:</span>
                          <span style={styles.scoreValue}>
                            {evaluacion.ponderado_final ? evaluacion.ponderado_final.toFixed(2) : 'N/A'}
                          </span>
                        </div>
                        <div style={styles.scoreItem}>
                          <span style={styles.scoreLabel}>👤 Autoevaluación:</span>
                          <span style={styles.scoreSubValue}>
                            {evaluacion.promedio_autoevaluacion ? evaluacion.promedio_autoevaluacion.toFixed(2) : 'N/A'}
                          </span>
                        </div>
                        <div style={styles.scoreItem}>
                          <span style={styles.scoreLabel}>👨‍💼 Supervisor:</span>
                          <span style={styles.scoreSubValue}>
                            {evaluacion.promedio_supervisor ? evaluacion.promedio_supervisor.toFixed(2) : 'N/A'}
                          </span>
                        </div>
                        {/* Mostrar diferencia */}
                        {evaluacion.promedio_autoevaluacion && evaluacion.promedio_supervisor && (
                          <div style={styles.scoreItem}>
                            <span style={styles.scoreLabel}>📊 Diferencia:</span>
                            <span style={{
                              ...styles.scoreSubValue,
                              color: Math.abs(evaluacion.promedio_autoevaluacion - evaluacion.promedio_supervisor) > 0.5 ? '#ef4444' : '#6b7280'
                            }}>
                              {(evaluacion.promedio_autoevaluacion - evaluacion.promedio_supervisor).toFixed(2)}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {/* Botón para generar PDF */}
                      <div style={styles.pdfButtonContainer}>
                        <button 
                          style={{
                            ...styles.pdfButton,
                            opacity: generandoPdf === evaluacion.id ? 0.7 : 1,
                            cursor: generandoPdf === evaluacion.id ? 'not-allowed' : 'pointer'
                          }}
                          onClick={(e) => handleGenerarPdfClick(evaluacion, e)}
                          disabled={generandoPdf === evaluacion.id}
                        >
                          {generandoPdf === evaluacion.id ? '📄 Generando...' : '📄 Generar PDF'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div style={styles.emptyState}>
                  <p>🔍 No hay evaluaciones 180° completadas en la base de datos</p>
                  <p style={{fontSize: '14px', color: '#64748b', marginTop: '8px'}}>
                    Las evaluaciones aparecerán aquí una vez que se procesen en eval180_terminada
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'analisis' && (
          <div style={styles.analisisContainer}>
            <h2 style={styles.sectionTitle}>Análisis Psicológico</h2>
            <div style={styles.analisisContent}>
              <div style={styles.analisisCard}>
                <h3>Competencias más Desarrolladas</h3>
                <p>Análisis de las competencias con mejor desempeño en la organización.</p>
              </div>
              <div style={styles.analisisCard}>
                <h3>Áreas de Mejora</h3>
                <p>Identificación de competencias que requieren desarrollo.</p>
              </div>
              <div style={styles.analisisCard}>
                <h3>Tendencias de Desempeño</h3>
                <p>Evolución del desempeño a lo largo del tiempo.</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'reportes' && (
          <div style={styles.reportesContainer}>
            <h2 style={styles.sectionTitle}>Reportes Psicológicos</h2>
            <div style={styles.reportesGrid}>
              <div style={styles.reporteCard}>
                <h3>Reporte Individual</h3>
                <p>Análisis detallado por empleado</p>
                <button style={styles.reporteButton}>Generar</button>
              </div>
              <div style={styles.reporteCard}>
                <h3>Reporte por Departamento</h3>
                <p>Análisis grupal por área</p>
                <button style={styles.reporteButton}>Generar</button>
              </div>
              <div style={styles.reporteCard}>
                <h3>Reporte Organizacional</h3>
                <p>Vista global de la organización</p>
                <button style={styles.reporteButton}>Generar</button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Modal de Detalle de Evaluación */}
      {showModal && selectedEvaluacion && (
        <div style={styles.modalOverlay} onClick={closeModal}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                Detalle de Evaluación - {selectedEvaluacion.empleado_nombre} {selectedEvaluacion.empleado_apellido || ''}
              </h2>
              <button style={styles.closeButton} onClick={closeModal}>
                ✕
              </button>
            </div>
            
            <div style={styles.modalBody}>
              {/* Información General */}
              <div style={styles.modalSection}>
                <h3 style={styles.sectionTitle}>Información General</h3>
                <div style={styles.infoGrid}>
                  <div style={styles.infoItem}>
                    <strong>Empleado:</strong> {selectedEvaluacion.empleado_nombre} {selectedEvaluacion.empleado_apellido || ''}
                  </div>
                  <div style={styles.infoItem}>
                    <strong>Cargo del Empleado:</strong> {selectedEvaluacion.empleado_cargo}
                  </div>
                  <div style={styles.infoItem}>
                    <strong>Cédula:</strong> {selectedEvaluacion.empleado_cedula}
                  </div>
                  <div style={styles.infoItem}>
                    <strong>Supervisor:</strong> {selectedEvaluacion.supervisor_nombre} {selectedEvaluacion.supervisor_apellido || ''}
                  </div>
                  <div style={styles.infoItem}>
                    <strong>Cargo del Supervisor:</strong> {selectedEvaluacion.supervisor_cargo || 'No especificado'}
                  </div>
                  <div style={styles.infoItem}>
                    <strong>Ponderado Final:</strong> 
                    <span style={styles.scoreHighlight}>
                      {selectedEvaluacion.ponderado_final?.toFixed(2) || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Competencias por Tipo */}
              <div style={styles.modalSection}>
                <h3 style={styles.sectionTitle}>Competencias por Tipo</h3>
                <div style={styles.competenciasGrid}>
                  {cargandoCompetencias ? (
                    <div style={styles.loadingContainer}>
                      <div style={styles.spinner}></div>
                      <p>Cargando competencias...</p>
                    </div>
                  ) : competenciasModal.length > 0 ? (
                    (() => {
                      // Agrupar competencias por tipo manteniendo el orden original
                      const competenciasPorTipo = {};
                      
                      competenciasModal.forEach(competencia => {
                        const tipo = competencia.tipo || 'sin_tipo';
                        
                        if (!competenciasPorTipo[tipo]) {
                          competenciasPorTipo[tipo] = [];
                        }
                        
                        competenciasPorTipo[tipo].push(competencia);
                      });

                      return Object.entries(competenciasPorTipo).map(([tipo, competenciasList]) => (
                        <div key={tipo} style={styles.competenciaCard}>
                          <h4 style={styles.competenciaTipo}>
                            {tipo.replace(/_/g, ' ').toUpperCase()} ({competenciasList.length})
                          </h4>
                          <div style={styles.competenciasList}>
                            {competenciasList.map((competencia) => (
                              <div key={competencia.id} style={styles.competenciaItem}>
                                <span style={styles.competenciaNombre}>{competencia.nombre}</span>
                                <div style={styles.competenciaScores}>
                                  <span style={styles.autoScore}>Auto: {competencia.autoScore}</span>
                                  <span style={styles.supervisorScore}>Sup: {competencia.supScore}</span>
                                  {competencia.analisisCritico?.esCritica && (
                                    <span style={{
                                      ...styles.criticaIndicator,
                                      backgroundColor: competencia.analisisCritico.color
                                    }}>
                                      ⚠️ {competencia.analisisCritico.nivelCriticidad}
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ));
                    })()
                  ) : (
                    <p style={styles.noData}>No se encontraron competencias para este empleado</p>
                  )}
                </div>
              </div>

              {/* Competencias Críticas */}
              <div style={styles.modalSection}>
                <h3 style={styles.sectionTitle}>Análisis Psicológico - Competencias Críticas</h3>
                <div style={styles.criticasContainer}>
                  {(() => {
                    const competenciasCriticas = competenciasModal.filter(comp => 
                      comp.analisisCritico?.esCritica
                    );

                    if (competenciasCriticas.length === 0) {
                      return (
                        <div style={styles.noCompetenciasCriticas}>
                          <p style={styles.noData}>
                            ✅ <strong>Excelente Autoconocimiento</strong>
                          </p>
                          <p style={styles.analisisPositivo}>
                            No se identificaron competencias críticas. El empleado demuestra un nivel saludable de 
                            autoconciencia y sus percepciones están alineadas con la evaluación supervisorial.
                          </p>
                        </div>
                      );
                    }

                    return (
                      <div style={styles.criticasList}>
                        <div style={styles.resumenCriticas}>
                          <p style={styles.contadorCriticas}>
                            <strong>{competenciasCriticas.length} competencia(s) requieren atención psicológica</strong>
                          </p>
                        </div>
                        
                        {competenciasCriticas.map((competencia) => {
                          const analisis = competencia.analisisCritico;
                          return (
                            <div key={competencia.id} style={{
                              ...styles.criticaItem,
                              borderLeft: `4px solid ${analisis.color}`
                            }}>
                              <div style={styles.criticaHeader}>
                                <div style={styles.criticaTituloContainer}>
                                  <strong>{competencia.nombre}</strong>
                                  <span style={styles.competenciaTipoBadge}>
                                    {competencia.tipo?.toUpperCase() || 'GENERAL'}
                                  </span>
                                </div>
                                <div style={styles.criticaMetrics}>
                                  <span style={{...styles.criticaScore, color: analisis.color}}>
                                    {analisis.tipoDiscrepancia}
                                  </span>
                                  <span style={styles.criticaDiferencia}>
                                    Δ {analisis.diferencia.toFixed(2)}
                                  </span>
                                </div>
                              </div>
                              
                              <div style={styles.criticaDetalle}>
                                <div style={styles.scoreComparison}>
                                  <span>Autoevaluación: <strong>{competencia.autoScore}</strong></span>
                                  <span>Supervisor: <strong>{competencia.supScore}</strong></span>
                                  <span style={{color: analisis.color}}>
                                    Nivel: <strong>{analisis.nivelCriticidad}</strong>
                                  </span>
                                </div>
                                <div style={styles.interpretacionContexto}>
                                  <small style={styles.contextoTexto}>
                                    📋 <strong>Contexto:</strong> {analisis.diferencia > 0 
                                      ? `El empleado se califica ${analisis.diferencia.toFixed(2)} puntos MÁS ALTO que su supervisor`
                                      : `El empleado se califica ${Math.abs(analisis.diferencia).toFixed(2)} puntos MÁS BAJO que su supervisor`
                                    } en esta competencia {analisis.tipoCompetencia}.
                                  </small>
                                </div>
                              </div>

                              <div style={styles.interpretacionPsicologica}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                  <h5 style={styles.interpretacionTitulo}>
                                    🧠 Interpretación Psicológica Específica:
                                  </h5>
                                  <button
                                    onClick={() => {
                                      if (modoEdicion.interpretacion[competencia.id]) {
                                        guardarCambiosPsicologo('interpretacion', competencia.id);
                                      } else {
                                        toggleEdicion('interpretacion', competencia.id);
                                        // Inicializar con el texto actual
                                        const textoActual = selectedEvaluacion.interpretaciones_psicologo?.[competencia.id] || analisis.interpretacion;
                                        setInterpretacionesEditadas(prev => ({
                                          ...prev,
                                          [competencia.id]: textoActual
                                        }));
                                      }
                                    }}
                                    disabled={guardandoCambios}
                                    style={{
                                      padding: '5px 10px',
                                      backgroundColor: modoEdicion.interpretacion[competencia.id] ? '#10b981' : '#3b82f6',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: guardandoCambios ? 'not-allowed' : 'pointer',
                                      fontSize: '12px'
                                    }}
                                  >
                                    {guardandoCambios ? 'Guardando...' : 
                                     modoEdicion.interpretacion[competencia.id] ? '💾 Guardar' : '✏️ Editar'}
                                  </button>
                                </div>
                                
                                {modoEdicion.interpretacion[competencia.id] ? (
                                  <textarea
                                    value={interpretacionesEditadas[competencia.id] || ''}
                                    onChange={(e) => handleInterpretacionChange(competencia.id, e.target.value)}
                                    style={{
                                      width: '100%',
                                      minHeight: '150px',
                                      padding: '10px',
                                      border: '1px solid #d1d5db',
                                      borderRadius: '4px',
                                      fontSize: '14px',
                                      fontFamily: 'inherit',
                                      lineHeight: '1.5',
                                      resize: 'vertical'
                                    }}
                                    placeholder="Escriba su interpretación psicológica específica..."
                                  />
                                ) : (
                                  <div style={styles.interpretacionTexto}>
                                    {(selectedEvaluacion.interpretaciones_psicologo?.[competencia.id] || analisis.interpretacion)
                                      .split('•').map((parte, index) => (
                                      <p key={index} style={styles.interpretacionParrafo}>
                                        {index === 0 ? parte : `• ${parte}`}
                                      </p>
                                    ))}
                                  </div>
                                )}
                              </div>

                              <div style={styles.recomendacionesPsicologicas}>
                                <h5 style={styles.recomendacionesTitulo}>
                                  💡 Plan de Intervención Especializado:
                                </h5>
                                <ul style={styles.recomendacionesList}>
                                  {analisis.recomendaciones.map((recomendacion, index) => (
                                    <li key={index} style={styles.recomendacionItem}>
                                      {recomendacion}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Plan de Mejoramiento */}
              <div style={styles.modalSection}>
                <h3 style={styles.sectionTitle}>Plan de Desarrollo Inteligente</h3>
                <div style={styles.planContainer}>
                  {(() => {
                    // Generar plan personalizado basado en análisis real de resultados
                    const planPersonalizado = generarPlanDesarrollo(selectedEvaluacion, competenciasModal);
                    
                    return (
                      <div style={styles.planContent}>
                        {/* Resumen Ejecutivo */}
                        <div style={styles.planHeader}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <h4 style={styles.planSubtitulo}>📋 Resumen Ejecutivo</h4>
                            <button
                              onClick={() => {
                                if (modoEdicion.resumenEjecutivo) {
                                  guardarCambiosPsicologo('resumenEjecutivo');
                                } else {
                                  toggleEdicion('resumenEjecutivo');
                                  // Inicializar con el texto actual
                                  const resumenActual = selectedEvaluacion.resumen_ejecutivo_psicologo || planPersonalizado.resumenEjecutivo;
                                  setResumenEjecutivoEditado(resumenActual);
                                }
                              }}
                              disabled={guardandoCambios}
                              style={{
                                padding: '5px 10px',
                                backgroundColor: modoEdicion.resumenEjecutivo ? '#10b981' : '#3b82f6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: guardandoCambios ? 'not-allowed' : 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              {guardandoCambios ? 'Guardando...' : 
                               modoEdicion.resumenEjecutivo ? '💾 Guardar' : '✏️ Editar'}
                            </button>
                          </div>
                          
                          {modoEdicion.resumenEjecutivo ? (
                            <textarea
                              value={resumenEjecutivoEditado}
                              onChange={(e) => handleResumenEjecutivoChange(e.target.value)}
                              style={{
                                width: '100%',
                                minHeight: '150px',
                                padding: '10px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                fontSize: '14px',
                                fontFamily: 'inherit',
                                lineHeight: '1.5',
                                resize: 'vertical'
                              }}
                              placeholder="Escriba el resumen ejecutivo personalizado..."
                            />
                          ) : (
                            <div style={styles.resumenEjecutivo}>
                              {selectedEvaluacion.resumen_ejecutivo_psicologo || planPersonalizado.resumenEjecutivo}
                            </div>
                          )}
                          
                          <div style={styles.planMetadatos}>
                            <span style={styles.planDuracion}>⏰ Duración: {planPersonalizado.duracion}</span>
                            <span style={styles.planPrioridad}>🚨 Prioridad: {planPersonalizado.prioridadDesarrollo}</span>
                            <span style={styles.planTipo}>👤 Perfil: {planPersonalizado.tipoEmpleado}</span>
                            <span style={styles.planNivel}>📊 Nivel: {planPersonalizado.nivelDesempeño}</span>
                          </div>
                        </div>

                        {/* Objetivos Específicos */}
                        <div style={styles.planSeccion}>
                          <h4 style={styles.planSubtitulo}>🎯 Objetivos Específicos de Desarrollo</h4>
                          <ul style={styles.planLista}>
                            {planPersonalizado.objetivosEspecificos.map((objetivo, index) => (
                              <li key={index} style={styles.planItem} className="plan-item">
                                {objetivo}
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Acciones de Desarrollo */}
                        <div style={styles.planSeccion}>
                          <h4 style={styles.planSubtitulo}>🔧 Acciones de Desarrollo Personalizadas</h4>
                          <ul style={styles.planLista}>
                            {planPersonalizado.accionesDesarrollo.map((accion, index) => (
                              <li key={index} style={styles.planItem} className="plan-item">
                                {accion}
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Metas Evaluables */}
                        <div style={styles.planSeccion}>
                          <h4 style={styles.planSubtitulo}>📊 Metas Evaluables y Medibles</h4>
                          <div style={styles.metasContainer}>
                            {planPersonalizado.metasEvaluables.map((meta, index) => (
                              <div key={index} style={styles.metaItem} className="meta-item">
                                {meta}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Cronograma de Implementación */}
                        <div style={styles.planSeccion}>
                          <h4 style={styles.planSubtitulo}>📅 Cronograma de Implementación</h4>
                          <div style={styles.cronogramaContainer}>
                            {planPersonalizado.cronogramaMensual.map((fase, index) => (
                              <div key={index} style={styles.cronogramaItem} className="cronograma-item">
                                {fase}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Indicadores de Seguimiento */}
                        {planPersonalizado.seguimientoRequerido && (
                          <div style={styles.planSeccionCritica}>
                            <h4 style={styles.planSubtitulo}>⚠️ Seguimiento Especializado Requerido</h4>
                            <div style={styles.seguimientoContainer}>
                              <p style={styles.seguimientoTexto}>
                                🔍 <strong>Atención:</strong> Este empleado requiere seguimiento más frecuente debido a las 
                                discrepancias identificadas en la evaluación 180°.
                              </p>
                              <ul style={styles.seguimientoLista}>
                                <li>📅 Reuniones de seguimiento quincenales (en lugar de mensuales)</li>
                                <li>📊 Evaluaciones de progreso cada 6 semanas</li>
                                <li>🎯 Feedback específico y continuo sobre competencias críticas</li>
                                <li>👥 Participación del supervisor directo en el proceso</li>
                              </ul>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Resumen de Calificaciones */}
              <div style={styles.modalSection}>
                <h3 style={styles.sectionTitle}>Resumen de Calificaciones</h3>
                <div style={styles.resumenGrid}>
                  <div style={styles.resumenCard}>
                    <h4>Autoevaluación</h4>
                    <div style={styles.resumenScore}>
                      {selectedEvaluacion.promedio_autoevaluacion?.toFixed(2) || 'N/A'}
                    </div>
                  </div>
                  <div style={styles.resumenCard}>
                    <h4>Evaluación Supervisor</h4>
                    <div style={styles.resumenScore}>
                      {selectedEvaluacion.promedio_supervisor?.toFixed(2) || 'N/A'}
                    </div>
                  </div>
                  <div style={styles.resumenCard}>
                    <h4>Diferencia</h4>
                    <div style={{
                      ...styles.resumenScore,
                      color: Math.abs((selectedEvaluacion.promedio_autoevaluacion || 0) - (selectedEvaluacion.promedio_supervisor || 0)) > 0.5 ? '#ef4444' : '#059669'
                    }}>
                      {((selectedEvaluacion.promedio_autoevaluacion || 0) - (selectedEvaluacion.promedio_supervisor || 0)).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  loadingContainer: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
  },
  loadingCard: {
    background: 'white',
    padding: '32px',
    borderRadius: '16px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
    textAlign: 'center'
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #e2e8f0',
    borderTop: '4px solid #667eea',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 16px'
  },
  loadingText: {
    color: '#64748b',
    margin: 0,
    fontSize: '16px'
  },
  header: {
    backgroundColor: 'white',
    borderBottom: '1px solid #e2e8f0',
    padding: '16px 24px',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: '1200px',
    margin: '0 auto'
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'column'
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: '700',
    color: '#1e293b'
  },
  subtitle: {
    margin: '4px 0 0 0',
    fontSize: '14px',
    color: '#64748b'
  },
  headerActions: {
    display: 'flex',
    gap: '12px'
  },
  logoutButton: {
    padding: '8px 16px',
    border: '1px solid #dc2626',
    backgroundColor: 'white',
    color: '#dc2626',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s'
  },
  navigation: {
    backgroundColor: 'white',
    borderBottom: '1px solid #e2e8f0',
    padding: '0 24px'
  },
  tabContainer: {
    display: 'flex',
    maxWidth: '1200px',
    margin: '0 auto'
  },
  tab: {
    padding: '16px 24px',
    border: 'none',
    backgroundColor: 'transparent',
    color: '#64748b',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    borderBottomWidth: '2px',
    borderBottomStyle: 'solid',
    borderBottomColor: 'transparent',
    transition: 'all 0.2s'
  },
  activeTab: {
    color: '#3b82f6',
    borderBottomColor: '#3b82f6'
  },
  main: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '24px'
  },
  overviewContainer: {
    marginBottom: '32px'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
    marginBottom: '32px'
  },
  statCard: {
    backgroundColor: 'white',
    padding: '24px',
    borderRadius: '12px',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    border: '1px solid #e2e8f0'
  },
  statTitle: {
    margin: '0 0 8px 0',
    fontSize: '14px',
    fontWeight: '500',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  statValue: {
    margin: 0,
    fontSize: '32px',
    fontWeight: '700',
    color: '#1e293b'
  },
  statSubtitle: {
    margin: '4px 0 0 0',
    fontSize: '12px',
    color: '#64748b',
    fontWeight: '400'
  },
  statBreakdown: {
    marginTop: '8px',
    paddingTop: '8px',
    borderTop: '1px solid #e2e8f0'
  },
  distributionContainer: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    marginTop: '24px'
  },
  distributionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px'
  },
  distributionCard: {
    padding: '20px',
    borderRadius: '8px',
    color: 'white',
    textAlign: 'center'
  },
  distributionTitle: {
    margin: '0 0 8px 0',
    fontSize: '14px',
    fontWeight: '500',
    opacity: '0.9'
  },
  distributionValue: {
    margin: 0,
    fontSize: '28px',
    fontWeight: '700'
  },
  sectionTitle: {
    margin: '0 0 24px 0',
    fontSize: '20px',
    fontWeight: '600',
    color: '#1e293b'
  },
  evaluacionesContainer: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
  },
  evaluacionesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  evaluacionItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    backgroundColor: '#f8fafc'
  },
  evaluacionInfo: {
    flex: 1
  },
  evaluacionTitulo: {
    margin: '0 0 4px 0',
    fontSize: '16px',
    fontWeight: '600',
    color: '#1e293b'
  },
  evaluacionDetalle: {
    margin: '0 0 8px 0',
    fontSize: '14px',
    color: '#64748b'
  },
  badge: {
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '500',
    color: 'white'
  },
  evaluacionScore: {
    textAlign: 'right',
    minWidth: '200px'
  },
  scoreDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  scoreItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px'
  },
  scoreLabel: {
    fontSize: '12px',
    color: '#64748b',
    fontWeight: '500'
  },
  scoreValue: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#059669'
  },
  scoreSubValue: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#6b7280'
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px',
    color: '#64748b'
  },
  analisisContainer: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
  },
  analisisContent: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '20px'
  },
  analisisCard: {
    padding: '20px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    backgroundColor: '#f8fafc'
  },
  reportesContainer: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
  },
  reportesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px'
  },
  reporteCard: {
    padding: '20px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    backgroundColor: '#f8fafc',
    textAlign: 'center'
  },
  reporteButton: {
    marginTop: '12px',
    padding: '8px 16px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500'
  },
  // Estilos del Modal
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px'
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: '12px',
    maxWidth: '1000px',
    maxHeight: '90vh',
    width: '100%',
    overflow: 'hidden',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px',
    borderBottom: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc'
  },
  modalTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '600',
    color: '#1e293b'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    color: '#64748b',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '4px',
    transition: 'color 0.2s'
  },
  modalBody: {
    padding: '24px',
    maxHeight: 'calc(90vh - 100px)',
    overflowY: 'auto'
  },
  modalSection: {
    marginBottom: '32px'
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '16px'
  },
  infoItem: {
    padding: '12px',
    backgroundColor: '#f1f5f9',
    borderRadius: '8px',
    fontSize: '14px'
  },
  scoreHighlight: {
    marginLeft: '8px',
    fontWeight: '700',
    color: '#059669',
    fontSize: '16px'
  },
  competenciasGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '20px'
  },
  competenciaCard: {
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '16px',
    backgroundColor: '#fafafa'
  },
  competenciaTipo: {
    margin: '0 0 12px 0',
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    textTransform: 'capitalize'
  },
  competenciasList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  competenciaItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px',
    backgroundColor: 'white',
    borderRadius: '4px',
    fontSize: '13px'
  },
  competenciaNombre: {
    flex: 1,
    textTransform: 'capitalize',
    color: '#374151'
  },
  competenciaScores: {
    display: 'flex',
    gap: '8px'
  },
  autoScore: {
    padding: '2px 6px',
    backgroundColor: '#dbeafe',
    color: '#1e40af',
    borderRadius: '4px',
    fontSize: '12px'
  },
  supervisorScore: {
    padding: '2px 6px',
    backgroundColor: '#dcfce7',
    color: '#166534',
    borderRadius: '4px',
    fontSize: '12px'
  },
  criticaIndicator: {
    padding: '2px 6px',
    color: 'white',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: 'bold',
    textTransform: 'uppercase'
  },
  noCompetenciasCriticas: {
    textAlign: 'center',
    padding: '20px',
    backgroundColor: '#f0fdf4',
    borderRadius: '8px',
    border: '1px solid #bbf7d0'
  },
  analisisPositivo: {
    color: '#166534',
    fontSize: '14px',
    marginTop: '8px',
    lineHeight: '1.5'
  },
  resumenCriticas: {
    backgroundColor: '#fef2f2',
    padding: '12px',
    borderRadius: '6px',
    marginBottom: '16px',
    border: '1px solid #fecaca'
  },
  contadorCriticas: {
    color: '#dc2626',
    fontSize: '14px',
    margin: 0
  },
  criticaMetrics: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '4px'
  },
  criticaDiferencia: {
    fontSize: '12px',
    color: '#6b7280',
    fontWeight: '500'
  },
  scoreComparison: {
    display: 'flex',
    gap: '16px',
    fontSize: '13px',
    color: '#374151',
    marginBottom: '12px'
  },
  interpretacionPsicologica: {
    backgroundColor: '#f8fafc',
    padding: '12px',
    borderRadius: '6px',
    marginBottom: '12px',
    border: '1px solid #e2e8f0'
  },
  interpretacionTitulo: {
    color: '#1e40af',
    fontSize: '13px',
    margin: '0 0 8px 0',
    fontWeight: '600'
  },
  interpretacionTexto: {
    fontSize: '12px',
    lineHeight: '1.5',
    color: '#374151'
  },
  interpretacionParrafo: {
    margin: '4px 0',
    textAlign: 'justify'
  },
  recomendacionesPsicologicas: {
    backgroundColor: '#fffbeb',
    padding: '12px',
    borderRadius: '6px',
    border: '1px solid #fed7aa'
  },
  recomendacionesTitulo: {
    color: '#92400e',
    fontSize: '13px',
    margin: '0 0 8px 0',
    fontWeight: '600'
  },
  recomendacionesList: {
    margin: 0,
    paddingLeft: '16px',
    fontSize: '12px',
    lineHeight: '1.5',
    color: '#374151'
  },
  recomendacionItem: {
    marginBottom: '4px'
  },
  criticasContainer: {
    backgroundColor: '#fef2f2',
    borderRadius: '8px',
    padding: '16px'
  },
  criticasList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  criticaItem: {
    backgroundColor: 'white',
    padding: '12px',
    borderRadius: '6px',
    border: '1px solid #fecaca'
  },
  criticaHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '1rem',
    flexWrap: 'wrap',
    gap: '0.5rem'
  },
  criticaScore: {
    fontWeight: '600',
    color: '#dc2626'
  },
  criticaDetalle: {
    display: 'flex',
    gap: '12px',
    fontSize: '12px',
    color: '#6b7280'
  },
  // Estilos para planes de desarrollo mejorados
  planHeader: {
    marginBottom: '1.5rem',
    padding: '1rem',
    backgroundColor: '#f0f9ff',
    borderRadius: '0.5rem',
    border: '1px solid #0ea5e9'
  },
  resumenEjecutivo: {
    fontSize: '0.875rem',
    color: '#374151',
    lineHeight: '1.6',
    marginBottom: '1rem',
    padding: '0.75rem',
    backgroundColor: 'white',
    borderRadius: '0.375rem',
    border: '1px solid #e2e8f0'
  },
  planMetadatos: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.75rem',
    marginTop: '0.75rem'
  },
  planDuracion: {
    fontSize: '0.75rem',
    padding: '0.25rem 0.5rem',
    backgroundColor: '#dbeafe',
    color: '#1e40af',
    borderRadius: '0.25rem',
    fontWeight: '500'
  },
  planPrioridad: {
    fontSize: '0.75rem',
    padding: '0.25rem 0.5rem',
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    borderRadius: '0.25rem',
    fontWeight: '500'
  },
  planTipo: {
    fontSize: '0.75rem',
    padding: '0.25rem 0.5rem',
    backgroundColor: '#f3e8ff',
    color: '#7c3aed',
    borderRadius: '0.25rem',
    fontWeight: '500'
  },
  planNivel: {
    fontSize: '0.75rem',
    padding: '0.25rem 0.5rem',
    backgroundColor: '#ecfdf5',
    color: '#059669',
    borderRadius: '0.25rem',
    fontWeight: '500'
  },
  metasContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem'
  },
  metaItem: {
    fontSize: '0.875rem',
    color: '#374151',
    lineHeight: '1.6',
    padding: '0.75rem',
    backgroundColor: '#fef3c7',
    borderRadius: '0.375rem',
    border: '1px solid #f59e0b',
    position: 'relative',
    paddingLeft: '1rem'
  },
  seguimientoContainer: {
    padding: '1rem',
    backgroundColor: '#fef2f2',
    borderRadius: '0.5rem',
    border: '1px solid #fecaca'
  },
  seguimientoTexto: {
    fontSize: '0.875rem',
    color: '#7f1d1d',
    marginBottom: '0.75rem',
    fontWeight: '500'
  },
  seguimientoLista: {
    margin: '0',
    paddingLeft: '1.25rem',
    color: '#7f1d1d'
  },
  planTipoContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '0.5rem'
  },
  planTipo: {
    fontSize: '0.875rem',
    fontWeight: '700',
    color: '#0c4a6e',
    backgroundColor: '#e0f2fe',
    padding: '0.25rem 0.75rem',
    borderRadius: '9999px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  planHorizonte: {
    fontSize: '0.75rem',
    color: '#64748b',
    backgroundColor: '#f1f5f9',
    padding: '0.25rem 0.5rem',
    borderRadius: '0.25rem',
    fontWeight: '500'
  },
  planComplejidad: {
    fontSize: '0.75rem',
    color: '#6b7280',
    fontStyle: 'italic',
    marginBottom: '0.5rem'
  },
  planEnfoque: {
    fontSize: '0.875rem',
    color: '#374151'
  },
  planSeccionCritica: {
    marginBottom: '1.5rem',
    padding: '1rem',
    backgroundColor: '#fef2f2',
    borderRadius: '0.5rem',
    border: '2px solid #fecaca'
  },
  planSeccion: {
    marginBottom: '1.5rem'
  },
  planSubtitulo: {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '0.75rem',
    paddingBottom: '0.5rem',
    borderBottom: '2px solid #e2e8f0'
  },
  planLista: {
    margin: '0',
    paddingLeft: '0',
    listStyle: 'none'
  },
  planItem: {
    fontSize: '0.875rem',
    color: '#374151',
    lineHeight: '1.6',
    marginBottom: '0.75rem',
    paddingLeft: '1.5rem',
    position: 'relative'
  },
  planItemCritico: {
    fontSize: '0.875rem',
    color: '#7f1d1d',
    lineHeight: '1.6',
    marginBottom: '0.75rem',
    paddingLeft: '1.5rem',
    position: 'relative',
    fontWeight: '500'
  },
  cronogramaContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem'
  },
  cronogramaItem: {
    fontSize: '0.875rem',
    color: '#374151',
    lineHeight: '1.6',
    padding: '0.75rem',
    backgroundColor: '#f8fafc',
    borderRadius: '0.375rem',
    border: '1px solid #e2e8f0',
    position: 'relative',
    paddingLeft: '1rem'
  },
  planNota: {
    marginTop: '1.5rem',
    padding: '1rem',
    backgroundColor: '#fffbeb',
    borderRadius: '0.5rem',
    border: '1px solid #fed7aa',
    fontSize: '0.8rem',
    color: '#92400e',
    lineHeight: '1.5'
  },
  planContainer: {
    backgroundColor: '#f0f9ff',
    borderRadius: '8px',
    padding: '16px'
  },
  planContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  noData: {
    color: '#6b7280',
    fontStyle: 'italic',
    textAlign: 'center',
    padding: '20px'
  },
  resumenGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px'
  },
  resumenCard: {
    backgroundColor: '#f8fafc',
    padding: '16px',
    borderRadius: '8px',
    textAlign: 'center',
    border: '1px solid #e2e8f0'
  },
  resumenScore: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#059669',
    marginTop: '8px'
  },
  // Estilos para el botón de PDF
  pdfButtonContainer: {
    marginTop: '15px',
    borderTop: '1px solid #e2e8f0',
    paddingTop: '15px'
  },
  pdfButton: {
    width: '100%',
    padding: '12px 16px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    boxShadow: '0 2px 4px rgba(59, 130, 246, 0.2)',
    ':hover': {
      backgroundColor: '#2563eb',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 8px rgba(59, 130, 246, 0.3)'
    },
    ':active': {
      transform: 'translateY(0)',
      boxShadow: '0 2px 4px rgba(59, 130, 246, 0.2)'
    }
  }
};

export default PsicologoDashboard;
